import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Platform, useWindowDimensions } from 'react-native';
import { DLOSimpleBreadcrumb } from '../../../../modules/dlo/components/DLOBreadcrumb';
import DLOModal from '../../../../modules/dlo/components/DLOModal';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import {
  setAsnLoadDetailsLoading,
  setAsnLoadDetailsData,
  setAsnLoadDetailsError,
  setAsnLoadDetailsPageNumber,
  setAsnLoadDetailsPageSize,
  setAsnLoadDetailsSort,
  setAsnLoadDetailsSearchText,
  setAsnLoadDetailsHasLoadedOnce,
  setAsnLoadDetailsLastRequest,
} from '../../../../store/slices/asnLoadDetailsSlice';
import { bumpAsnLocationSummaryApplyVersion } from '../../../../store/slices/asnLocationSummarySlice';
import { useLoadDetailsExport } from './hooks/useLoadDetailsExport';
import { useLocationSummaryExport } from './hooks/useLocationSummaryExport';
import { AsnMetricColumn } from './constants/asnOnePagerConstants';
import { getValidIconName } from '../../../AnalyticsHub/utils/reportsTabHelpers';
import { fetchPeriodWeeks } from '../../../../api/periodWeeksApi';
import { getPreviousPeriodAndWeek } from '../../../../utils/filterOptions';
import { fetchAsnOverviewKpis, ASNOverviewKpiRequest, ASNOverviewKpiResponse } from './api/asnOverviewKpiApi';
import {
  fetchAsnBottomPerformingNonAsn,
  ASNBottomPerformingRequest,
  ASNBottomPerformingResponse,
} from './api/asnOverviewKpiApi';
import {
  fetchAsnNonAsnLoadTrend,
  ASNNonAsnLoadTrendRequest,
  ASNNonAsnLoadTrendResponse,
} from './api/asnOverviewKpiApi';
import {
  fetchLoadsVsNonAsnChart,
  LoadsVsNonAsnChartRequest,
  LoadsVsNonAsnChartResponse,
} from './api/asnOverviewKpiApi';
import { fetchAsnLocationDropdownFilters } from './api/asnLocationDropdownApi';
import {
  fetchAsnLoadDetails,
  ASNLoadDetailsRequest,
  AsnLocationGrainKey,
} from './api/asnOverviewKpiApi';
import {
  buildAsnOverviewKpiRequest,
  buildAsnBottomPerformingRequest,
  buildAsnNonAsnLoadTrendRequest,
  buildAsnLoadsVsNonAsnChartRequest,
  buildAsnLoadDetailsRequest,
  AsnOverviewKpiRequestInputs,
} from './utils/asnOverviewKpiRequestBuilder';
import {
  computeBackDrillState,
  computeBarClickDrillState,
  AsnLoadsChartItem,
} from './utils/asnLoadsComparisonAdapter';
import { createRequestGuard } from './utils/requestGuard';
import KPIFilterDropdown, { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import DeliveryDateRangeFilter, { DeliveryDateRange } from '../../components/DeliveryDateRangeFilter';
import { FontFamily } from '../../../../constants/fonts';
import { useDisabledPeriodWeekIds } from '../../../AnalyticsHub/hooks/useDisabledPeriodWeekIds';
import { useAdminLocations } from '../../../AnalyticsHub/hooks/useAdminLocations';
import { useAsnLocationDropdowns } from './hooks/useAsnLocationDropdowns';
import { formatToLocalWithTZ } from '../../constants/dateHelpers';
import Overview from './components/Overview';
import LocationSummary from './components/LocationSummary';
import LocationSummaryV2 from './components/LocationSummaryV2';
import LoadDetails from './components/LoadDetails';

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_LINK = '/navigator-reports/asn-gap';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

type FilterKey =
  | 'region'
  | 'market'
  | 'location'
  | 'year'
  | 'period'
  | 'week'
  | 'deliveryDate'
  | 'shippingLocation'
  | 'receivingLocation';
type AsnGapTab = 'overview' | 'location-summary' | 'location-summary-v2' | 'load-details';
type QuickSelectKey = 'wtd' | 'ptd' | 'qtd' | 'ytd' | 'lcw' | 'lcp';
type ChartHierarchyLevel = 'region' | 'market' | 'location';
type ChartTimeframeGrain = 'year' | 'period' | 'week';

const CHART_HIERARCHY_OPTIONS: { key: ChartHierarchyLevel; label: string }[] = [
  { key: 'region', label: 'Region / Unit' },
  { key: 'market', label: 'Market / POD' },
  { key: 'location', label: 'Location' },
];

const CHART_TIMEFRAME_OPTIONS: { key: ChartTimeframeGrain; label: string }[] = [
  { key: 'year', label: 'Year' },
  { key: 'period', label: 'Period' },
  { key: 'week', label: 'Week' },
];

// Number of weeks in a period (fixed 4-4-4-4 / 13-period PepsiCo calendar).
const WEEKS_PER_PERIOD = 4;
// Quarters group periods as [1-3], [4-6], [7-9], [10-13].
const QUARTER_START_PERIODS = [1, 4, 7, 10];

const QUICK_SELECT_OPTIONS: { key: QuickSelectKey; label: string }[] = [
  { key: 'wtd', label: 'Week-to-Date' },
  { key: 'ptd', label: 'Period-to-Date' },
  { key: 'qtd', label: 'Quarter-to-Date' },
  { key: 'ytd', label: 'Year-to-Date' },
  { key: 'lcw', label: 'Last Closed Week' },
  { key: 'lcp', label: 'Last Closed Period' },
];

const toISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isConsecutiveNumericSelection = (values: number[]): boolean => {
  const uniq = Array.from(new Set(values.filter((n) => Number.isFinite(n)))).sort((a, b) => a - b);
  if (uniq.length <= 1) return true;
  return uniq[uniq.length - 1] - uniq[0] + 1 === uniq.length;
};

export interface AsnGapFilterParams {
  region?: string;
  market?: string;
  location?: string;
  year?: string;
  period?: string;
  week?: string;
  startDate?: string;
  endDate?: string;
  shippingLocation?: string;
  receivingLocation?: string;
  caseQtyMin?: string;
  caseQtyMax?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AsnGapDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web' && windowWidth < 768;

  const [mobileFilterOverlayOpen, setMobileFilterOverlayOpen] = useState(false);

  const mobileLabelMap: Record<QuickSelectKey, string> = {
    'lcw': 'LCW',
    'lcp': 'LCP',
    'ptd': 'PTD',
    'wtd': 'WTD',
    'qtd': 'QTD',
    'ytd': 'YTD',
  };

  const reportConfig = useSelector((state: RootState) =>
    state.reportingConfiguration.rows.find((row) => row.link === REPORT_LINK)
  );

  const iconName = getValidIconName(reportConfig?.icon || 'assessment');
  const title = 'ASN Gap Report';

  const [activeTab, setActiveTab] = useState<AsnGapTab>('overview');

  const tabs = useMemo(
    () => [
      { key: 'overview' as const, label: 'Overview' },
      //{ key: 'location-summary' as const, label: 'Location Summary' },
      { key: 'location-summary-v2' as const, label: 'Location Summary' },
      { key: 'load-details' as const, label: 'Load Details' },
    ],
    []
  );

  // ─── Selected location from Redux header (pre-populate on mount) ──────────
  const headerSelectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);

  const initialLocationFromHeader = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!headerSelectedLocation) return [];
    const name = String(headerSelectedLocation.displayText || headerSelectedLocation.name || '').trim();
    if (!name) return [];
    return [{ id: String(headerSelectedLocation.id || headerSelectedLocation.locationId || name), displayText: name }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const hasAutoPopulatedRef = React.useRef(initialLocationFromHeader.length > 0);

  const initialPeriodFromCurrentWeek = useMemo<KPIFilterDropdownItem[]>(() => {
    if (!currentPeriodWeek?.period) return [];
    // Week 1 of a period has no data yet, so default to the previous period instead.
    const defaultPeriod =
      Number(currentPeriodWeek.week) === 1
        ? getPreviousPeriodAndWeek(currentPeriodWeek.period, currentPeriodWeek.week).period
        : String(currentPeriodWeek.period);
    return [{ id: `P${defaultPeriod}`, displayText: `P${defaultPeriod}` }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAutoPopulatedPeriodRef = React.useRef(initialPeriodFromCurrentWeek.length > 0);

  // Reactive readiness flag — mirrors hasAutoPopulatedPeriodRef but as state
  // so changes propagate to LocationSummary/LocationSummaryV2 as a prop,
  // preventing them from firing their initial API call before the current
  // period has been resolved and set on selectedPeriods.
  const [isFilterReady, setIsFilterReady] = React.useState(
    initialPeriodFromCurrentWeek.length > 0,
  );

  // ─── Filter state ───────────────────────────────────────────────────────
  const [openFilterKey, setOpenFilterKey] = useState<FilterKey | null>(null);

  const [selectedRegions, setSelectedRegions] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedMarkets, setSelectedMarkets] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<KPIFilterDropdownItem[]>(
    () => initialLocationFromHeader,
  );
  const [selectedYears, setSelectedYears] = useState<KPIFilterDropdownItem[]>(
    () => [{ id: String(currentYear), displayText: String(currentYear) }],
  );
  const [selectedPeriods, setSelectedPeriods] = useState<KPIFilterDropdownItem[]>(
    () => initialPeriodFromCurrentWeek,
  );
  const [selectedWeeks, setSelectedWeeks] = useState<KPIFilterDropdownItem[]>([]);
  const [deliveryDateRange, setDeliveryDateRange] = useState<DeliveryDateRange>({ start: null, end: null });
  const [activeQuickSelect, setActiveQuickSelect] = useState<QuickSelectKey | null>(null);
  const [selectedShippingLocations, setSelectedShippingLocations] = useState<KPIFilterDropdownItem[]>([]);
  const [selectedReceivingLocations, setSelectedReceivingLocations] = useState<KPIFilterDropdownItem[]>([]);
  const [caseQtyMin, setCaseQtyMin] = useState('');
  const [caseQtyMax, setCaseQtyMax] = useState('');

  // Validate case quantity range
  const caseQtyError = useMemo(() => {
    if (!caseQtyMin || !caseQtyMax) return null;
    const minNum = Number(caseQtyMin);
    const maxNum = Number(caseQtyMax);
    if (minNum > maxNum) {
      return 'Minimum must be less than or equal to maximum';
    }
    return null;
  }, [caseQtyMin, caseQtyMax]);

  // ─── Chart Configuration (Overview tab only) ────────────────────────────
  const [isChartConfigExpanded, setIsChartConfigExpanded] = useState(false);
  const [chartHierarchyLevel, setChartHierarchyLevel] = useState<ChartHierarchyLevel>('region');
  const [chartTimeframeGrain, setChartTimeframeGrain] = useState<ChartTimeframeGrain>('period');

  // Applied filters shared across all three tabs
  const [appliedFilterParams, setAppliedFilterParams] = useState<AsnGapFilterParams>(() => {
    const params: AsnGapFilterParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      params.location = initialLocationFromHeader.map((l) => l.displayText).join(',');
    }
    if (initialPeriodFromCurrentWeek.length > 0) {
      params.period = initialPeriodFromCurrentWeek.map((period) => String(period.id).replace(/^P/i, '')).join(',');
    }
    return params;
  });
  const [manualBaselineParams, setManualBaselineParams] = useState<AsnGapFilterParams>(appliedFilterParams);

  // ─── Overview tab KPI cards (POST /api/asn/overview/kpis) ────────────────
  // AsnGapDashboard is the single owner of this request: it already owns the
  // filter Apply/Reset/quick-select handlers below, so the KPI fetch is
  // triggered explicitly from those handlers (plus once on mount) rather than
  // from an effect keyed on filter state — this avoids any risk of a second,
  // independent trigger (e.g. from Overview itself) causing duplicate calls.
  const [kpiData, setKpiData] = useState<ASNOverviewKpiResponse | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiErrorModal, setKpiErrorModal] = useState<{ visible: boolean; title: string; message: string }>({
    visible: false,
    title: '',
    message: '',
  });

  const dismissKpiError = () => setKpiErrorModal((prev) => ({ ...prev, visible: false }));

  const loadAsnOverviewKpis = useCallback(async (request: ASNOverviewKpiRequest) => {
    setKpiLoading(true);
    try {
      const data = await fetchAsnOverviewKpis(request);
      setKpiData(data);
      if (data?.supportingMetrics?.dataError) {
        setKpiErrorModal({
          visible: true,
          title: 'ASN Gap KPI Data Unavailable',
          message: 'Some ASN Gap KPI values could not be calculated for the selected filters due to a data quality issue.',
        });
      }
    } catch (err: any) {
      // Never fall back to fixture/stale data — the cards render "—" while kpiData is null.
      setKpiData(null);
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while loading the ASN Gap Overview KPIs.';
      setKpiErrorModal({ visible: true, title: 'ASN Gap KPI Request Failed', message: msg });
    } finally {
      setKpiLoading(false);
    }
  }, []);

  // ─── Bottom Performing Non-ASN (Overview tab ranking table) ────────────
  // Owned/fetched here (mirrors the KPI cards above) so there is a single
  // request owner shared across initial load, Apply, Reset, and Chart
  // Configuration → Location Hierarchy changes (which fire immediately,
  // independent of Apply). Regular filter dropdown selections (region,
  // market, location, year, period, week, delivery date) do NOT auto-fire
  // this request — they only take effect when the user clicks Apply.
  const [bottomPerformingData, setBottomPerformingData] = useState<ASNBottomPerformingResponse | null>(null);
  const [bottomPerformingLoading, setBottomPerformingLoading] = useState(false);
  const [bottomPerformingError, setBottomPerformingError] = useState<string | null>(null);
  const [bottomPerformingSortColumn, setBottomPerformingSortColumn] = useState<string>('NON_ASN_LOADS');
  const [bottomPerformingSortDirection, setBottomPerformingSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  const loadBottomPerformingNonAsn = useCallback(async (request: ASNBottomPerformingRequest) => {
    setBottomPerformingLoading(true);
    setBottomPerformingError(null);
    try {
      const data = await fetchAsnBottomPerformingNonAsn(request);
      setBottomPerformingData(data);
    } catch (err: any) {
      // Never fall back to fixture data — the card renders its own empty/error
      // state while bottomPerformingData is null.
      setBottomPerformingData(null);
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while loading the Bottom Performing Non-ASN data.';
      setBottomPerformingError(msg);
    } finally {
      setBottomPerformingLoading(false);
    }
  }, []);

  const handleBottomPerformingSortChange = useCallback(
    (sortColumn: string, sortDirection: 'ASC' | 'DESC') => {
      setBottomPerformingSortColumn(sortColumn);
      setBottomPerformingSortDirection(sortDirection);
      loadBottomPerformingNonAsn(
        buildAsnBottomPerformingRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: selectedYears,
          periods: selectedPeriods,
          weeks: selectedWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          locationHierarchy: chartHierarchyLevel,
          sortColumn,
          sortDirection,
        }),
      );
    },
    [
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
      chartHierarchyLevel,
      loadBottomPerformingNonAsn,
    ],
  );

  // ─── Non-ASN Load % Trend (Overview tab chart) ─────────────────────────
  // Owned/fetched here (mirrors the KPI cards / Bottom Performing table
  // above) so there is a single request owner shared across initial load,
  // Apply, Reset, and Chart Configuration → Timeframe changes (which fire
  // immediately, independent of Apply). Regular filter dropdown selections
  // do NOT auto-fire this request — they only take effect when the user
  // clicks Apply.
  const [trendData, setTrendData] = useState<ASNNonAsnLoadTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const loadNonAsnLoadTrend = useCallback(async (request: ASNNonAsnLoadTrendRequest) => {
    setTrendLoading(true);
    setTrendError(null);
    try {
      const data = await fetchAsnNonAsnLoadTrend(request);
      setTrendData(data);
    } catch (err: any) {
      // Never fall back to fixture data — the chart renders its own empty/error
      // state while trendData is null.
      setTrendData(null);
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while loading the Non-ASN Load % Trend data.';
      setTrendError(msg);
    } finally {
      setTrendLoading(false);
    }
  }, []);

  const handleBottomPerformingRetry = useCallback(() => {
    loadBottomPerformingNonAsn(
      buildAsnBottomPerformingRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        locationHierarchy: chartHierarchyLevel,
        sortColumn: bottomPerformingSortColumn,
        sortDirection: bottomPerformingSortDirection,
      }),
    );
  }, [
    loadBottomPerformingNonAsn,
    selectedRegions,
    selectedMarkets,
    selectedShippingLocations,
    selectedReceivingLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    deliveryDateRange,
    caseQtyMin,
    caseQtyMax,
    chartHierarchyLevel,
    bottomPerformingSortColumn,
    bottomPerformingSortDirection,
  ]);

  const handleTrendRetry = useCallback(() => {
    loadNonAsnLoadTrend(
      buildAsnNonAsnLoadTrendRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        timeframeGrain: chartTimeframeGrain,
      }),
    );
  }, [
    loadNonAsnLoadTrend,
    selectedRegions,
    selectedMarkets,
    selectedShippingLocations,
    selectedReceivingLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    deliveryDateRange,
    caseQtyMin,
    caseQtyMax,
    chartTimeframeGrain,
  ]);

  // ─── "ASN Loads vs Non-ASN Loads" chart (POST /asn/loads-vs-non-asn/chart) ───
  // Owned/fetched here (mirrors the KPI cards / Bottom Performing / Trend
  // requests above) — single request owner. The request always carries the
  // current `chartHierarchyLevel` as `locationHierarchy` (mapped to
  // REGION/MARKET/LOCATION), and a Chart Configuration → Location Hierarchy
  // change re-fetches immediately (see the effect keyed on
  // `chartHierarchyLevel` below) so the chart always reflects the grain the
  // backend actually grouped by. In-chart drill-down (a bar click) stays a
  // purely client-side filter of the already-fetched response
  // (`getVisibleLoadsVsNonAsnItems`) and never triggers another request.
  // `chartDrillRegion`/`chartDrillMarket` are the only new pieces of state
  // this feature adds (the selected parent lineage for scoped drill-down);
  // they are NOT a duplicate of `chartHierarchyLevel`.
  const [loadsVsNonAsnData, setLoadsVsNonAsnData] = useState<LoadsVsNonAsnChartResponse | null>(null);
  const [loadsVsNonAsnLoading, setLoadsVsNonAsnLoading] = useState(false);
  const [loadsVsNonAsnError, setLoadsVsNonAsnError] = useState<string | null>(null);
  const [chartDrillRegion, setChartDrillRegion] = useState<string | null>(null);
  const [chartDrillMarket, setChartDrillMarket] = useState<string | null>(null);
  // Tracks the chart's current drill depth for rendering (canGoBack, visible
  // rows, hierarchy label in Overview). Driven exclusively by bar-press /
  // back / navigate-to-level; never written by the Chart Config panel.
  // Resets to 'region' on Reset and when "Region / Unit" is selected.
  const [chartDrillDepth, setChartDrillDepth] = useState<ChartHierarchyLevel>('region');

  // Authoritative Market/Location list for the current chart drill level,
  // sourced from the ASN location dropdown API (`fetchAsnLocationDropdownFilters`
  // — the same Region -> Market -> Location relationship the shared filter
  // dropdowns already use), rather than from whatever grain values happen to
  // appear in the flat Loads vs Non-ASN chart response. This is a fully
  // separate, independent fetch — it never touches `selectedRegions` /
  // `selectedMarkets` / `selectedShippingLocations` (the main filter
  // dropdown state) or the `useAsnLocationDropdowns` hook those drive.
  //   'region' level -> null (no cross-reference needed; every level-0 row
  //     is already a top-level bucket the chart API itself grouped by).
  //   'market' level -> the Markets under `chartDrillRegion` (or every
  //     Market when no region has been drilled into yet).
  //   'location' level -> the Locations under `chartDrillRegion` /
  //     `chartDrillMarket` (either may be unset).
  // An empty array (dropdown API found nothing for the current path) means
  // the chart shows its existing empty state instead of any bar.
  const [chartDrillOptionValues, setChartDrillOptionValues] = useState<string[] | null>(null);
  const [chartDrillOptionsLoading, setChartDrillOptionsLoading] = useState(false);
  const chartDrillOptionsGuardRef = useRef(createRequestGuard());

  useEffect(() => {
    if (activeTab !== 'overview') return;

    // Use chartDrillDepth (the visual drill level) rather than chartHierarchyLevel
    // (the Chart Config panel value) — after a bar press the two diverge.
    if (chartDrillDepth === 'region') {
      setChartDrillOptionValues(null);
      setChartDrillOptionsLoading(false);
      return;
    }

    // For a direct "Location" panel selection with no drill path, the
    // cross-reference fetch has no useful parent context — calling
    // fetchAsnLocationDropdownFilters with empty region/market returns
    // results that do not reliably match the chart rows' grainVals.location
    // identifiers, which would cause filterChartItemsByValidValues to
    // incorrectly filter out every row and show the empty state.
    // Pass null (= no constraint) so every returned row is rendered.
    if (chartDrillDepth === 'location' && !chartDrillRegion) {
      setChartDrillOptionValues(null);
      setChartDrillOptionsLoading(false);
      return;
    }

    const guard = chartDrillOptionsGuardRef.current;
    const requestId = guard.next();
    setChartDrillOptionsLoading(true);

    fetchAsnLocationDropdownFilters(
      chartDrillDepth === 'market'
        ? { shippingRegions: chartDrillRegion ? [chartDrillRegion] : [] }
        : {
            shippingRegions: chartDrillRegion ? [chartDrillRegion] : [],
            shippingMarkets: chartDrillMarket ? [chartDrillMarket] : [],
          },
    )
      .then((res) => {
        if (!guard.isCurrent(requestId)) return; // a newer request has since started — discard
        setChartDrillOptionValues(
          chartDrillDepth === 'market'
            ? res.shippingMarkets.map((opt) => opt.value)
            : res.shippingLocations.map((opt) => opt.locId),
        );
      })
      .catch(() => {
        if (!guard.isCurrent(requestId)) return;
        // Never fall back to the unfiltered chart rows — treat a failed
        // lookup the same as "no related rows found" (existing empty state).
        setChartDrillOptionValues([]);
      })
      .finally(() => {
        if (guard.isCurrent(requestId)) setChartDrillOptionsLoading(false);
      });
  }, [activeTab, chartDrillDepth, chartDrillRegion, chartDrillMarket]);

  const [chartSortBy, setChartSortBy] = useState<LoadsVsNonAsnChartRequest['sortBy']>('');
  const [chartSortDirection, setChartSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [chartSearchQuery, setChartSearchQuery] = useState('');

  // Stale-response guard: only the most-recently-issued request is allowed
  // to write into state — an earlier, slower request that resolves after a
  // newer one must be silently discarded.
  const loadsVsNonAsnRequestGuardRef = useRef(createRequestGuard());

  const loadLoadsVsNonAsnChart = useCallback(async (request: LoadsVsNonAsnChartRequest) => {
    const guard = loadsVsNonAsnRequestGuardRef.current;
    const requestId = guard.next();
    setLoadsVsNonAsnLoading(true);
    setLoadsVsNonAsnError(null);
    try {
      const data = await fetchLoadsVsNonAsnChart(request);
      if (!guard.isCurrent(requestId)) return; // a newer request has since started — discard

      // Validate current drill path against the newly fetched dataset.
      // Two distinct cases for non-region levels:
      //   (a) Direct selection — user tapped "Market"/"Location" in the config
      //       panel with no existing drill path (drillRegion/drillMarket null).
      //       Any row at the expected hierarchyLevel is sufficient; keep it.
      //   (b) Drill-path — user clicked a bar (drillRegion/drillMarket set).
      //       Validate the specific parent still exists; reset if stale.
      // Empty/null rows → keepHierarchy stays false → reset fires (empty-state).
      // Validate against chartDrillDepth — the level the API was actually
      // asked to return rows at (used as locationHierarchy in the request).
      let keepHierarchy = false;
      if (data?.rows?.length) {
        if (chartDrillDepth === 'region') {
          keepHierarchy = true;
        } else if (chartDrillDepth === 'market') {
          if (chartDrillRegion) {
            // Drill-path: validate the specific region still appears
            keepHierarchy = data.rows.some(
              (row) => row.hierarchyLevel === 1 && row.parentGrainVals?.region === chartDrillRegion,
            );
          } else {
            // Direct selection: any market-level row is sufficient
            keepHierarchy = data.rows.some((row) => row.hierarchyLevel === 1);
          }
        } else if (chartDrillDepth === 'location') {
          if (chartDrillRegion && chartDrillMarket) {
            // Full drill-path: validate both region and market still appear
            keepHierarchy = data.rows.some(
              (row) =>
                row.hierarchyLevel === 2 &&
                row.parentGrainVals?.region === chartDrillRegion &&
                row.parentGrainVals?.market === chartDrillMarket,
            );
          } else if (chartDrillRegion) {
            // Partial drill-path (region known, market unset): validate region appears
            keepHierarchy = data.rows.some(
              (row) => row.hierarchyLevel === 2 && row.parentGrainVals?.region === chartDrillRegion,
            );
          } else {
            // Direct selection: any location-level row is sufficient
            keepHierarchy = data.rows.some((row) => row.hierarchyLevel === 2);
          }
        }
      }

      if (!keepHierarchy) {
        setChartHierarchyLevel('region');
        setChartDrillDepth('region');
        setChartDrillRegion(null);
        setChartDrillMarket(null);
      }

      setLoadsVsNonAsnData(data);
    } catch (err: any) {
      if (!guard.isCurrent(requestId)) return;
      // Never fall back to fixture data — the chart renders its own empty/error
      // state while loadsVsNonAsnData is null.
      setLoadsVsNonAsnData(null);
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while loading the ASN Loads vs Non-ASN Loads chart.';
      setLoadsVsNonAsnError(msg);
    } finally {
      if (guard.isCurrent(requestId)) setLoadsVsNonAsnLoading(false);
    }
  }, [chartDrillDepth, chartDrillRegion, chartDrillMarket]);

  // A Region or Market bar was clicked — drill in (never navigate to
  // another tab). `chartDrillDepth`, `chartDrillRegion`, and `chartDrillMarket`
  // are updated. `chartHierarchyLevel` (Chart Configuration) is never written
  // so the segmented control always reflects the user's explicit choice.
  // The re-fetch effect still fires because `chartDrillRegion`/`chartDrillMarket`
  // change.
  const handleLoadsVsNonAsnBarPress = useCallback((item: AsnLoadsChartItem) => {
    const next = computeBarClickDrillState(
      { hierarchyLevel: chartDrillDepth, drillRegion: chartDrillRegion, drillMarket: chartDrillMarket },
      item,
    );
    setChartDrillDepth(next.hierarchyLevel);
    setChartDrillRegion(next.drillRegion);
    setChartDrillMarket(next.drillMarket);
  }, [chartDrillDepth, chartDrillRegion, chartDrillMarket]);

  // Back link: Location -> Market -> Region. Uses `chartDrillDepth` (not
  // `chartHierarchyLevel`) to know the current drill level; never writes to
  // `chartHierarchyLevel` so the Chart Config panel is unaffected.
  const handleLoadsVsNonAsnBack = useCallback(() => {
    const next = computeBackDrillState({
      hierarchyLevel: chartDrillDepth,
      drillRegion: chartDrillRegion,
      drillMarket: chartDrillMarket,
    });
    setChartDrillDepth(next.hierarchyLevel);
    setChartDrillRegion(next.drillRegion);
    setChartDrillMarket(next.drillMarket);
  }, [chartDrillDepth, chartDrillRegion, chartDrillMarket]);

  // Navigate to a specific drill level (breadcrumb / legend tap). Sets drill
  // depth and path; never touches `chartHierarchyLevel`.
  const handleLoadsVsNonAsnNavigateToLevel = useCallback((level: ChartHierarchyLevel, region: string | null, market: string | null) => {
    setChartDrillDepth(level);
    setChartDrillRegion(region);
    setChartDrillMarket(market);
  }, []);

  const handleLoadsVsNonAsnSortChange = useCallback((sortBy: LoadsVsNonAsnChartRequest['sortBy']) => {
    setChartSortBy(sortBy);
    setChartSortDirection(sortBy === 'label' ? 'ASC' : 'DESC');
  }, []);

  const handleLoadsVsNonAsnRetry = useCallback(() => {
    loadLoadsVsNonAsnChart(
      buildAsnLoadsVsNonAsnChartRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        sortBy: chartSortBy,
        sortDirection: chartSortDirection,
        searchQuery: chartSearchQuery,
        locationHierarchy: chartHierarchyLevel,
        parentGrain: chartDrillMarket ? 'market' : (chartDrillRegion ? 'region' : undefined),
        parentGrainVals: chartDrillMarket && chartDrillRegion
          ? { region: chartDrillRegion, market: chartDrillMarket }
          : (chartDrillRegion ? { region: chartDrillRegion } : undefined),
      }),
    );
  }, [
    loadLoadsVsNonAsnChart,
    selectedRegions,
    selectedMarkets,
    selectedShippingLocations,
    selectedReceivingLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    deliveryDateRange,
    caseQtyMin,
    caseQtyMax,
    chartSortBy,
    chartSortDirection,
    chartSearchQuery,
    chartHierarchyLevel,
    chartDrillRegion,
    chartDrillMarket,
  ]);

  // Chart Configuration → Location Hierarchy was selected directly (not via
  // a bar click). Both `chartHierarchyLevel` (the panel) and `chartDrillDepth`
  // (what Overview renders and what the re-fetch effect watches) are set to the
  // chosen level. Selecting "Region / Unit" also clears the drill path;
  // selecting Market/Location leaves any existing drill path untouched.
  const handleChartHierarchySelect = useCallback((level: ChartHierarchyLevel) => {
    setChartHierarchyLevel(level);
    setChartDrillDepth(level);
    if (level === 'region') {
      setChartDrillRegion(null);
      setChartDrillMarket(null);
    }
  }, []);


  // Owned/fetched here (mirrors the Overview tab's KPI/Bottom Performing/
  // Trend requests above) so the same filter state and Apply/Reset flow is
  // reused. Unlike those, this request also depends on the table's own
  // pagination/sort/search state. The API response and that pagination/sort
  // /search state both live in the asnLoadDetails Redux slice (rather than
  // local component state) so the last-fetched page survives this component
  // unmounting (e.g. navigating away and back) and is available anywhere
  // else in the app without prop-drilling.
  const {
    data: loadDetailsData,
    loading: loadDetailsLoading,
    error: loadDetailsError,
    pageNumber: ldPageNumber,
    pageSize: ldPageSize,
    sortBy: ldSortBy,
    sortDirection: ldSortDirection,
    searchText: ldSearchText,
    hasLoadedOnce: hasLoadedLoadDetailsOnce,
    lastRequest: lastLoadDetailsRequest,
  } = useSelector((state: RootState) => state.asnLoadDetails);

  // ─── Load Details → Export to Excel ─────────────────────────────────
  // Fetches every page for lastLoadDetailsRequest (the currently applied
  // filters/sort/search — never a not-yet-applied dropdown edit) and
  // downloads the combined rows as an .xlsx file. Does not touch the
  // table's own data/pagination state.
  const { isExporting: isExportingLoadDetails, exportLoadDetails } = useLoadDetailsExport();
  const handleExportLoadDetails = useCallback(() => {
    if (!lastLoadDetailsRequest) return;
    exportLoadDetails(lastLoadDetailsRequest);
  }, [lastLoadDetailsRequest, exportLoadDetails]);

  // ─── Location Summary V2 → Export to Excel ───────────────────────────
  // Mirrors the Load Details export pattern exactly: reads the last-applied
  // request from Redux (so it always reflects committed filters, sort, search,
  // and grain selections — never a not-yet-applied dropdown edit), fetches
  // all pages via fetchAsnLocationDetailsAllPages, and downloads the combined
  // rows as an .xlsx workbook.
  const lastLocationSummaryRequest = useSelector(
    (state: RootState) => state.asnLocationSummary.lastRequest,
  );
  const { isExporting: isExportingLocationSummaryV2, exportLocationSummary } =
    useLocationSummaryExport();
  const handleExportLocationSummaryV2 = useCallback(
    (visibleMetricColumns: AsnMetricColumn[], visibleGrainKeys: AsnLocationGrainKey[]) => {
      if (!lastLocationSummaryRequest) return;
      exportLocationSummary(
        lastLocationSummaryRequest,
        visibleGrainKeys,
        visibleMetricColumns,
      );
    },
    [lastLocationSummaryRequest, exportLocationSummary],
  );

  // Guards against an older, slower request overwriting a newer response —
  // only the response matching the latest fired request id is applied.
  const loadDetailsRequestIdRef = React.useRef(0);

  const loadAsnLoadDetails = useCallback(async (request: ASNLoadDetailsRequest) => {
    const requestId = ++loadDetailsRequestIdRef.current;
    dispatch(setAsnLoadDetailsLastRequest(request));
    dispatch(setAsnLoadDetailsLoading());
    try {
      const data = await fetchAsnLoadDetails(request);
      if (requestId !== loadDetailsRequestIdRef.current) return; // stale response — ignore
      dispatch(setAsnLoadDetailsData(data));
    } catch (err: any) {
      if (requestId !== loadDetailsRequestIdRef.current) return; // stale response — ignore
      // Never fall back to fixture/mock data — the table renders its own
      // empty/error state while loadDetailsData is null.
      const msg = err?.response?.data?.message
        || err?.message
        || 'An unexpected error occurred while loading the ASN Gap Load Details.';
      dispatch(setAsnLoadDetailsError(msg));
    }
  }, [dispatch]);

  // Fires the first time the Load Details tab becomes active (first-ever
  // activation). The hasLoadedOnce flag lives in Redux so the cached data
  // survives the component unmounting and can be shown immediately on
  // re-visits without a loading flash.
  useEffect(() => {
    if (activeTab !== 'load-details' || hasLoadedLoadDetailsOnce) return;
    dispatch(setAsnLoadDetailsHasLoadedOnce());
    loadAsnLoadDetails(
      buildAsnLoadDetailsRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        pageNumber: 1,
        pageSize: ldPageSize,
        sortBy: ldSortBy,
        sortDirection: ldSortDirection,
        searchText: ldSearchText,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasLoadedLoadDetailsOnce]);

  // Re-fetches Load Details whenever the user switches back to that tab after
  // the data has already been loaded once — ensures the table always reflects
  // the current applied filter values, not a stale prior fetch.
  // Skipped on the very first activation (handled by the effect above).
  const isFirstLoadDetailsTabActivation = React.useRef(true);
  useEffect(() => {
    if (activeTab !== 'load-details') return;
    if (!hasLoadedLoadDetailsOnce) return; // first activation handled above
    if (isFirstLoadDetailsTabActivation.current) {
      // On the initial render where the tab is already 'load-details', the
      // first-activation effect fires — don't double-fetch here.
      isFirstLoadDetailsTabActivation.current = false;
      return;
    }
    loadAsnLoadDetails(
      buildAsnLoadDetailsRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        pageNumber: 1,
        pageSize: ldPageSize,
        sortBy: ldSortBy,
        sortDirection: ldSortDirection,
        searchText: ldSearchText,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Re-fetches Location Summary whenever the user switches to either
  // location-summary tab — bumping applyVersion signals the child component
  // to re-fetch with the current filter values.
  const isFirstLocationSummaryTabActivation = React.useRef(true);
  useEffect(() => {
    if (activeTab !== 'location-summary' && activeTab !== 'location-summary-v2') return;
    if (isFirstLocationSummaryTabActivation.current) {
      isFirstLocationSummaryTabActivation.current = false;
      return;
    }
    dispatch(bumpAsnLocationSummaryApplyVersion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleLoadDetailsPageChange = useCallback(
    (pageNumber: number) => {
      dispatch(setAsnLoadDetailsPageNumber(pageNumber));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: selectedYears,
          periods: selectedPeriods,
          weeks: selectedWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          pageNumber,
          pageSize: ldPageSize,
          sortBy: ldSortBy,
          sortDirection: ldSortDirection,
          searchText: ldSearchText,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
      ldPageSize,
      ldSortBy,
      ldSortDirection,
      ldSearchText,
    ],
  );

  const handleLoadDetailsPageSizeChange = useCallback(
    (pageSize: number) => {
      dispatch(setAsnLoadDetailsPageSize(pageSize));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: selectedYears,
          periods: selectedPeriods,
          weeks: selectedWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          pageNumber: 1,
          pageSize,
          sortBy: ldSortBy,
          sortDirection: ldSortDirection,
          searchText: ldSearchText,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
      ldSortBy,
      ldSortDirection,
      ldSearchText,
    ],
  );

  const handleLoadDetailsSortChange = useCallback(
    (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
      dispatch(setAsnLoadDetailsSort({ sortBy, sortDirection }));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: selectedYears,
          periods: selectedPeriods,
          weeks: selectedWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          pageNumber: ldPageNumber,
          pageSize: ldPageSize,
          sortBy,
          sortDirection,
          searchText: ldSearchText,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
      ldPageNumber,
      ldPageSize,
      ldSearchText,
    ],
  );

  const handleLoadDetailsSearchChange = useCallback(
    (searchText: string) => {
      dispatch(setAsnLoadDetailsSearchText(searchText));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: selectedYears,
          periods: selectedPeriods,
          weeks: selectedWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          pageNumber: ldPageNumber,
          pageSize: ldPageSize,
          sortBy: ldSortBy,
          sortDirection: ldSortDirection,
          searchText,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
      ldPageNumber,
      ldPageSize,
      ldSortBy,
      ldSortDirection,
    ],
  );

  // Suppresses Effect A from re-firing when Reset restores chartTimeframeGrain
  // to its default — Reset already issues all four loader calls explicitly.
  const skipOverviewLoadEffectRef = React.useRef(false);

  // Fires all four Overview loaders whenever: (a) isFilterReady first becomes
  // true, (b) chartTimeframeGrain changes, or (c) the user switches to the
  // Overview tab — ensuring data always reflects current filter values on
  // activation. Never fires before the period filter is populated.
  useEffect(() => {
    if (!isFilterReady) return;
    if (activeTab !== 'overview') return;
    if (skipOverviewLoadEffectRef.current) {
      skipOverviewLoadEffectRef.current = false;
      return;
    }
    loadAsnOverviewKpis(
      buildAsnOverviewKpiRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
      }),
    );
    loadBottomPerformingNonAsn(
      buildAsnBottomPerformingRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        locationHierarchy: chartHierarchyLevel,
        sortColumn: bottomPerformingSortColumn,
        sortDirection: bottomPerformingSortDirection,
      }),
    );
    loadNonAsnLoadTrend(
      buildAsnNonAsnLoadTrendRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        timeframeGrain: chartTimeframeGrain,
      }),
    );
    loadLoadsVsNonAsnChart(
      buildAsnLoadsVsNonAsnChartRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        sortBy: chartSortBy,
        sortDirection: chartSortDirection,
        searchQuery: chartSearchQuery,
        locationHierarchy: chartHierarchyLevel,
        parentGrain: chartDrillMarket ? 'market' : (chartDrillRegion ? 'region' : undefined),
        parentGrainVals: chartDrillMarket && chartDrillRegion
          ? { region: chartDrillRegion, market: chartDrillMarket }
          : (chartDrillRegion ? { region: chartDrillRegion } : undefined),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterReady, chartTimeframeGrain, activeTab]);

  // Re-fire the overview load once the current period/week has loaded so
  // the default Period filter (unavailable at mount before the redux fetch resolves)
  // gets applied, matching the Location default-population pattern above.
  useEffect(() => {
    if (hasAutoPopulatedPeriodRef.current) return;
    if (!currentPeriodWeek?.period) return;
    hasAutoPopulatedPeriodRef.current = true;
    // Unblock the location-summary initial load — it deferred until this fires.
    setIsFilterReady(true);

    const defaultPeriod =
      Number(currentPeriodWeek.week) === 1
        ? getPreviousPeriodAndWeek(currentPeriodWeek.period, currentPeriodWeek.week).period
        : String(currentPeriodWeek.period);
    const periodItem = { id: `P${defaultPeriod}`, displayText: `P${defaultPeriod}` };
    setSelectedPeriods([periodItem]);

    const resolvedParams: AsnGapFilterParams = {
      ...appliedFilterParams,
      period: defaultPeriod,
    };
    setAppliedFilterParams(resolvedParams);
    setManualBaselineParams(resolvedParams);

    // If the user is already on a location-summary tab when the period
    // resolves, bump applyVersion so the component re-fetches with the
    // correct period (guards against the rare race where Trigger 1 fired
    // before this effect ran and therefore used an empty period array).
    if (activeTab === 'location-summary' || activeTab === 'location-summary-v2') {
      dispatch(bumpAsnLocationSummaryApplyVersion());
    }

    // The Overview tab's initial-load effect is now gated on isFilterReady
    // (set to true just above). React batches setIsFilterReady(true) and
    // setSelectedPeriods([periodItem]) in the same flush, so when that
    // effect re-runs it will already have the correct period in selectedPeriods.
    // No separate API calls are needed here for the overview tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPeriodWeek]);

  // Re-fetch the "ASN Loads vs Non-ASN Loads" chart when its own sort or
  // search control changes, on a Region/Market drill (bar click), or on a
  // Chart Configuration → Location Hierarchy change (direct selection) —
  // fires immediately per spec, independent of the Apply button.
  // Gated on isFilterReady so it never fires with an empty period filter
  // during the period-loading window (before currentPeriodWeek resolves).
  const isFirstLoadsVsNonAsnSortSearchRun = React.useRef(true);
  // Set by Reset to skip this effect's fetch when Reset already issued one.
  const skipLoadsVsNonAsnSortSearchDrillEffectRef = React.useRef(false);
  useEffect(() => {
    if (isFirstLoadsVsNonAsnSortSearchRun.current) {
      isFirstLoadsVsNonAsnSortSearchRun.current = false;
      return;
    }
    if (!isFilterReady) return;
    if (skipLoadsVsNonAsnSortSearchDrillEffectRef.current) {
      skipLoadsVsNonAsnSortSearchDrillEffectRef.current = false;
      return;
    }
    loadLoadsVsNonAsnChart(
      buildAsnLoadsVsNonAsnChartRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        sortBy: chartSortBy,
        sortDirection: chartSortDirection,
        searchQuery: chartSearchQuery,
        // Use chartDrillDepth so the API returns rows at the level Overview
        // expects to render — chartHierarchyLevel (Chart Config) stays fixed
        // at the user's panel selection and never changes on bar press.
        locationHierarchy: chartDrillDepth,
        parentGrain: chartDrillMarket ? 'market' : (chartDrillRegion ? 'region' : undefined),
        parentGrainVals: chartDrillMarket && chartDrillRegion
          ? { region: chartDrillRegion, market: chartDrillMarket }
          : (chartDrillRegion ? { region: chartDrillRegion } : undefined),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSortBy, chartSortDirection, chartSearchQuery, chartDrillRegion, chartDrillMarket, chartDrillDepth]);

  // Re-fetch Bottom Performing Non-ASN on a Chart Configuration → Location
  // Hierarchy change only — fires immediately, independent of the Apply
  // button, per the original spec. All other filter dropdowns (region,
  // market, location, year, period, week, delivery date) are intentionally
  // NOT watched here — those only refresh Bottom Performing when the user
  // explicitly clicks Apply (see handleApplyFilters) or Reset, not on every
  // dropdown selection.
  // Gated on isFilterReady so it never fires with an empty period filter
  // during the period-loading window (before currentPeriodWeek resolves).
  const isFirstBottomPerformingGrainRun = React.useRef(true);
  // Set by Reset to skip this effect's fetch when Reset already issued one.
  const skipBottomPerformingGrainEffectRef = React.useRef(false);
  useEffect(() => {
    if (isFirstBottomPerformingGrainRun.current) {
      isFirstBottomPerformingGrainRun.current = false;
      return;
    }
    if (!isFilterReady) return;
    if (skipBottomPerformingGrainEffectRef.current) {
      skipBottomPerformingGrainEffectRef.current = false;
      return;
    }
    loadBottomPerformingNonAsn(
      buildAsnBottomPerformingRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: selectedYears,
        periods: selectedPeriods,
        weeks: selectedWeeks,
        deliveryDateRange,
        caseQtyMin,
        caseQtyMax,
        locationHierarchy: chartHierarchyLevel,
        sortColumn: bottomPerformingSortColumn,
        sortDirection: bottomPerformingSortDirection,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartHierarchyLevel]);

  // ─── Location Summary tab (POST /api/asn/location-details) ──────────────
  // The shared filter bar's current selections are owned entirely by this
  // component (above) — LocationSummary reuses them as a read-only prop
  // (never duplicated into its own state) to build its request alongside
  // its own perspective/grainSelections/sort/search/pagination state.
  const asnFilterInputs = useMemo<AsnOverviewKpiRequestInputs>(
    () => ({
      regions: selectedRegions,
      markets: selectedMarkets,
      shippingLocations: selectedShippingLocations,
      receivingLocations: selectedReceivingLocations,
      years: selectedYears,
      periods: selectedPeriods,
      weeks: selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
    }),
    [
      selectedRegions,
      selectedMarkets,
      selectedShippingLocations,
      selectedReceivingLocations,
      selectedYears,
      selectedPeriods,
      selectedWeeks,
      deliveryDateRange,
      caseQtyMin,
      caseQtyMax,
    ],
  );

  // ─── Locations from cached Admin Locations flow ──────────────────────────
  const { locations: adminLocations } = useAdminLocations();

  const selectedRegionIds: string[] = useMemo(() => {
    if (selectedRegions.length === 0) return ['all'];
    return selectedRegions.map((r) => normalizeFilterValue(r.id));
  }, [selectedRegions]);

  const selectedMarketIds: string[] = useMemo(() => {
    if (selectedMarkets.length === 0) return ['all'];
    return selectedMarkets.map((m) => normalizeFilterValue(m.id));
  }, [selectedMarkets]);

  // ─── ASN Location Dropdown Filters API (replaces adminLocations-based filtering) ───

  const {
    regionOptions,
    marketOptions,
    locationOptions,
    receivingLocationOptions,
    loading: dropdownsLoading,
    error: dropdownsError,
    refreshDropdowns,
    lastRefreshedAt,
  } = useAsnLocationDropdowns(
    selectedRegions,
    selectedMarkets,
    selectedShippingLocations,
    { debounceMs: 300, enabled: true, skipInitialFetch: true }
  );

  const lastRefreshedLabel = useMemo(
    () => formatToLocalWithTZ(lastRefreshedAt),
    [lastRefreshedAt]
  );

  // Note: selectedRegionIds and selectedMarketIds are now only used for compatibility
  // The hook handles all cascading logic via API calls

  useEffect(() => {
    if (!locationOptions || locationOptions.length === 0) return;

    setSelectedLocations((prev) => {
      if (!prev || prev.length === 0) return prev;

      const validItems = prev.filter((selectedLoc) =>
        locationOptions.some((opt) => String(opt.id) === String(selectedLoc.id))
      );
      if (validItems.length > 0) return validItems;

      const sel = prev[0];
      const match = locationOptions.find((opt) =>
        String(opt.id) === String((sel as any).locationId) ||
        normalizeFilterValue(opt.displayText) === normalizeFilterValue(sel.displayText) ||
        normalizeFilterValue(opt.displayText) === normalizeFilterValue((sel as any).name || '')
      );

      if (match) return [match];

      return [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationOptions]);

  useEffect(() => {
    if (hasAutoPopulatedRef.current) return; // only auto-populate once
    if (!headerSelectedLocation) return;
    if (!locationOptions || locationOptions.length === 0) return;

    const locId = String(headerSelectedLocation.id || headerSelectedLocation.locationId || '');
    const locName = headerSelectedLocation.displayText || headerSelectedLocation.name || '';

    const matchingOption = locationOptions.find(
      (opt) => String(opt.id) === locId || opt.displayText === locName || normalizeFilterValue(opt.displayText) === normalizeFilterValue(locName)
    );

    if (matchingOption) {
      setSelectedLocations([matchingOption]);
      hasAutoPopulatedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSelectedLocation, locationOptions]);

  // ─── Trigger first API call after auto-population or on initial load ──────
  // When skipInitialFetch is true, we need to manually trigger the first API call
  // after auto-population has set the selectedLocations (if there's a header location),
  // OR on initial page load (if there's no header location).
  // This ensures dropdown options are available on page load.
  useEffect(() => {
    // Always trigger the initial fetch:
    // 1. If auto-populated: after the location is set
    // 2. If not auto-populated: immediately on mount to populate with all options
    if (hasAutoPopulatedRef.current || initialLocationFromHeader.length === 0) {
      refreshDropdowns();
    }
  }, []); // Run only once on mount

  // ─── Year options (2025 up to current year, most recent first) ─────────
  const FIRST_ANALYTICS_YEAR = 2025;
  const yearOptions: KPIFilterDropdownItem[] = useMemo(() => {
    const count = Math.max(1, currentYear - FIRST_ANALYTICS_YEAR + 1);
    return Array.from({ length: count }, (_, i) => ({
      id: String(currentYear - i),
      displayText: String(currentYear - i),
    }));
  }, [currentYear]);

  // ─── Period & Week: fetched directly from /period-weeks API ─────────────────
  const selectedYearNumbers = useMemo(
    () => selectedYears.map((y) => Number(y.id)).filter((n) => !isNaN(n)),
    [selectedYears],
  );
  const [rawPeriodWeeksData, setRawPeriodWeeksData] = useState<any[]>([]);

  useEffect(() => {
    if (selectedYearNumbers.length === 0) {
      setRawPeriodWeeksData([]);
      return;
    }
    let isMounted = true;
    fetchPeriodWeeks(selectedYearNumbers)
      .then((res) => { if (isMounted) setRawPeriodWeeksData(res.data ?? []); })
      .catch(() => { if (isMounted) setRawPeriodWeeksData([]); });
    return () => { isMounted = false; };
  }, [selectedYearNumbers]);

  const periodOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      return Array.from({ length: 13 }, (_, i) => ({ id: `P${i + 1}`, displayText: `P${i + 1}` }));
    }
    const periodSet = new Set<number>();
    rawPeriodWeeksData.forEach((yd: any) => {
      (yd.periods ?? []).forEach((pd: any) => {
        if (pd.period != null) periodSet.add(Number(pd.period));
      });
    });
    return Array.from(periodSet)
      .sort((a, b) => a - b)
      .map((p) => ({ id: `P${p}`, displayText: `P${p}` }));
  }, [rawPeriodWeeksData]);

  const weekOptions: KPIFilterDropdownItem[] = useMemo(() => {
    if (rawPeriodWeeksData.length === 0) {
      return [];
    }
    const activePeriodIds = selectedPeriods.map((p) => String(p.id));
    const items: KPIFilterDropdownItem[] = [];

    rawPeriodWeeksData.forEach((yd: any) => {
      (yd.periods ?? []).forEach((pd: any) => {
        const periodNum = Number(pd.period);
        if (activePeriodIds.length > 0 && !activePeriodIds.includes(`P${periodNum}`)) return;
        (pd.weeks ?? []).forEach((week: any, idx: number) => {
          const weekInYear = typeof week === 'number'
            ? week
            : Number(week?.weekInYear ?? week?.week ?? idx + 1);
          const weekInPeriod = idx + 1;
          items.push({
            id: `P${periodNum}W${weekInPeriod}`,
            displayText: `W${weekInYear} (P${periodNum}W${weekInPeriod})`,
            weekInYearNum: weekInYear,
          });
        });
      });
    });

    const uniqueMap = new Map<string, KPIFilterDropdownItem>();
    items.forEach((it) => {
      const key = String(it.id);
      if (!uniqueMap.has(key)) uniqueMap.set(key, it);
    });
    const uniqueItems = Array.from(uniqueMap.values());

    return uniqueItems.sort((a, b) => {
      const aNum = Number((a as any).weekInYearNum) || 0;
      const bNum = Number((b as any).weekInYearNum) || 0;
      return aNum - bNum;
    });
  }, [rawPeriodWeeksData, selectedPeriods]);

  const currentPeriodWeek = useSelector((state: RootState) => state.appData?.periodWeek?.data);

  const getScopedDateRangeFromPeriodWeeks = useCallback(
    (
      yearNumbers: number[],
      periodNumbers: number[] = [],
      weekEntries: { period: number; weekInPeriod: number }[] = [],
    ): { start: string; end: string } | null => {
      if (rawPeriodWeeksData.length === 0 || yearNumbers.length === 0) return null;

      const yearSet = new Set(yearNumbers.filter((n) => Number.isFinite(n)));
      const periodSet = periodNumbers.length > 0 ? new Set(periodNumbers.filter((n) => Number.isFinite(n))) : null;
      const weekSet =
        weekEntries.length > 0
          ? new Set(weekEntries.map(({ period, weekInPeriod }) => `${period}:${weekInPeriod}`))
          : null;

      const dates: string[] = [];

      rawPeriodWeeksData.forEach((yd: any) => {
        if (yearSet.size > 0 && !yearSet.has(Number(yd.year))) return;

        (yd.periods ?? []).forEach((pd: any) => {
          const periodNum = Number(pd.period);
          if (periodSet && !periodSet.has(periodNum)) return;

          (pd.weeks ?? []).forEach((weekObj: any) => {
            const weekInPeriod = Number(weekObj.week ?? weekObj.weekInPeriod ?? 0);
            if (weekSet && !weekSet.has(`${periodNum}:${weekInPeriod}`)) return;

            (weekObj.days ?? []).forEach((day: any) => {
              if (day?.date) dates.push(day.date);
            });
          });
        });
      });

      if (dates.length === 0) return null;
      dates.sort();
      return { start: dates[0], end: dates[dates.length - 1] };
    },
    [rawPeriodWeeksData],
  );

  const constrainedDeliveryDateScope = useMemo<{ start: string; end: string } | null>(() => {
    if (selectedYears.length !== 1) return null;
    const selectedYear = Number(selectedYears[0]?.id);
    if (!Number.isFinite(selectedYear)) return null;
    const selectedPeriodNumbers = selectedPeriods
      .map((p) => Number(String(p.id).replace(/^P/i, '')))
      .filter((n) => Number.isFinite(n));
    const selectedWeekEntries = selectedWeeks
      .map((w) => {
        const idMatch = String(w.id).match(/^P(\d+)W(\d+)$/i);
        const textMatch = String(w.displayText).match(/\(P(\d+)W(\d+)\)/i);
        const periodNum = Number(idMatch?.[1] ?? textMatch?.[1]);
        const weekInPeriodNum = Number(idMatch?.[2] ?? textMatch?.[2]);
        if (!Number.isFinite(periodNum) || !Number.isFinite(weekInPeriodNum)) return null;
        return { period: periodNum, weekInPeriod: weekInPeriodNum };
      })
      .filter((entry): entry is { period: number; weekInPeriod: number } => entry !== null);

    return getScopedDateRangeFromPeriodWeeks([selectedYear], selectedPeriodNumbers, selectedWeekEntries);
  }, [selectedYears, selectedWeeks, selectedPeriods, getScopedDateRangeFromPeriodWeeks]);

  const hasDiscontinuousTimeframeSelection = useMemo(() => {
    const selectedPeriodNumbers = selectedPeriods
      .map((p) => Number(String(p.id).replace(/^P/i, '')))
      .filter((n) => Number.isFinite(n));

    const selectedWeekSequence = selectedWeeks
      .map((w) => {
        const idMatch = String(w.id).match(/^P(\d+)W(\d+)$/i);
        const periodNum = Number(idMatch?.[1]);
        const weekInPeriodNum = Number(idMatch?.[2]);
        if (!Number.isFinite(periodNum) || !Number.isFinite(weekInPeriodNum)) return NaN;
        return (periodNum - 1) * WEEKS_PER_PERIOD + weekInPeriodNum;
      })
      .filter((n) => Number.isFinite(n));

    if ((selectedPeriodNumbers.length > 0 || selectedWeekSequence.length > 0) && selectedYears.length !== 1) {
      return true;
    }

    if (!isConsecutiveNumericSelection(selectedPeriodNumbers)) return true;
    if (!isConsecutiveNumericSelection(selectedWeekSequence)) return true;
    return false;
  }, [selectedPeriods, selectedWeeks, selectedYears]);

  const deliveryDateDisabledReason = hasDiscontinuousTimeframeSelection
    ? 'Select a consecutive timeframe.'
    : null;

  useEffect(() => {
    if (!hasDiscontinuousTimeframeSelection) return;
    if (!deliveryDateRange.start && !deliveryDateRange.end) return;
    setDeliveryDateRange({ start: null, end: null });
  }, [hasDiscontinuousTimeframeSelection, deliveryDateRange.start, deliveryDateRange.end]);

  useEffect(() => {
    if (activeQuickSelect) return;
    if (hasDiscontinuousTimeframeSelection) return;
    if (selectedPeriods.length === 0 && selectedWeeks.length === 0) return;
    if (!constrainedDeliveryDateScope) return;

    const { start, end } = constrainedDeliveryDateScope;
    if (deliveryDateRange.start === start && deliveryDateRange.end === end) return;
    setDeliveryDateRange({ start, end });
  }, [
    activeQuickSelect,
    hasDiscontinuousTimeframeSelection,
    constrainedDeliveryDateScope,
    selectedPeriods,
    selectedWeeks,
  ]);

  const isDeliveryDateOutsideConstrainedScope = useMemo(() => {
    if (!constrainedDeliveryDateScope) return false;
    const { start, end } = constrainedDeliveryDateScope;
    const datesToValidate = [deliveryDateRange.start, deliveryDateRange.end].filter(Boolean) as string[];
    if (datesToValidate.length === 0) return false;
    return datesToValidate.some((d) => d < start || d > end);
  }, [constrainedDeliveryDateScope, deliveryDateRange]);

  const { disabledPeriodIds } = useDisabledPeriodWeekIds({
    currentPeriodWeek,
    selectedYearNumbers,
    periodOptions,
    weekOptions,
  });

  const disabledWeekIds = useMemo(() => {
    if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return new Set<string>();
    if (selectedYearNumbers.length !== 1 || selectedYearNumbers[0] !== currentPeriodWeek.year) return new Set<string>();
    const currentWeekInYear = (currentPeriodWeek.period - 1) * 4 + currentPeriodWeek.week;
    const disabled = new Set<string>();
    for (const opt of weekOptions) {
      const weekInYearNum = Number((opt as any).weekInYearNum);
      if (Number.isFinite(weekInYearNum) && weekInYearNum > currentWeekInYear) {
        disabled.add(String(opt.id));
      }
    }
    return disabled;
  }, [currentPeriodWeek, selectedYearNumbers, weekOptions]);

  // ─── Cascading: invalidate downstream when upstream changes ─────────────

  const handleRegionChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedRegions(value);
    setSelectedMarkets([]);
    setSelectedLocations([]);
  };

  const handleMarketChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedMarkets(value);
    setSelectedLocations([]);
  };

  const handleLocationChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedLocations(value);
    setSelectedReceivingLocations([]);
  };

  const handleShippingLocationChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedShippingLocations(value);
    setSelectedReceivingLocations([]);
  };

  const handleYearChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedYears(value);
    setSelectedPeriods([]);
    setSelectedWeeks([]);
    setActiveQuickSelect(null);
  };

  const handlePeriodChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedPeriods(value);
    setSelectedWeeks([]);
    setActiveQuickSelect(null);
  };

  const handleWeekChange = (value: KPIFilterDropdownItem[]) => {
    setSelectedWeeks(value);
    setActiveQuickSelect(null);
  };

  const handleDeliveryDateChange = (value: DeliveryDateRange) => {
    if (hasDiscontinuousTimeframeSelection) return;
    const clamp = (iso: string | null): string | null => {
      if (!iso || !constrainedDeliveryDateScope) return iso;
      if (iso < constrainedDeliveryDateScope.start) return constrainedDeliveryDateScope.start;
      if (iso > constrainedDeliveryDateScope.end) return constrainedDeliveryDateScope.end;
      return iso;
    };
    setDeliveryDateRange({
      start: clamp(value.start),
      end: clamp(value.end),
    });
    setActiveQuickSelect(null);
  };

  const handleResetFilters = () => {
    const resetYear: KPIFilterDropdownItem = { id: String(currentYear), displayText: String(currentYear) };
    setSelectedRegions([]);
    setSelectedMarkets([]);
    setSelectedLocations(initialLocationFromHeader);
    setSelectedYears([resetYear]);
    setSelectedPeriods(initialPeriodFromCurrentWeek);
    setSelectedWeeks([]);
    setDeliveryDateRange({ start: null, end: null });
    setActiveQuickSelect(null);
    setSelectedShippingLocations([]);
    setSelectedReceivingLocations([]);
    setCaseQtyMin('');
    setCaseQtyMax('');
    const resetParams: AsnGapFilterParams = { year: String(currentYear) };
    if (initialLocationFromHeader.length > 0) {
      resetParams.location = initialLocationFromHeader.map((l) => l.displayText).join(',');
    }
    if (initialPeriodFromCurrentWeek.length > 0) {
      resetParams.period = initialPeriodFromCurrentWeek.map((period) => String(period.id).replace(/^P/i, '')).join(',');
    }
    setAppliedFilterParams(resetParams);
    setManualBaselineParams(resetParams);

    // Only refresh the Overview tab's KPI cards / Bottom Performing / Trend
    // requests when the Overview tab is the one currently active — Reset is
    // fired from the shared filter bar, but Location Summary and Load
    // Details have no use for these three responses, so don't waste a
    // network call fetching them while the user is on another tab.
    if (activeTab === 'overview') {
      // Refresh the KPI cards using the just-restored defaults (not the stale
      // pre-reset selections — none of the arrays below read component state).
      loadAsnOverviewKpis(
        buildAsnOverviewKpiRequest({
          regions: [],
          markets: [],
          shippingLocations: [],
          receivingLocations: [],
          years: [resetYear],
          periods: initialPeriodFromCurrentWeek,
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin: '',
          caseQtyMax: '',
        }),
      );
      // Reset also restores Chart Configuration (Location Hierarchy,
      // Timeframe, drill path, chart sort/search) to defaults — the guard
      // refs below stop the matching auto-fire effects from double-fetching.
      if (chartHierarchyLevel !== 'region') skipBottomPerformingGrainEffectRef.current = true;
      if (chartTimeframeGrain !== 'period') skipOverviewLoadEffectRef.current = true;
      if (
        chartHierarchyLevel !== 'region' ||
        chartSortBy !== '' ||
        chartSortDirection !== 'DESC' ||
        chartSearchQuery !== '' ||
        chartDrillRegion !== null ||
        chartDrillMarket !== null
      ) {
        skipLoadsVsNonAsnSortSearchDrillEffectRef.current = true;
      }
      setChartHierarchyLevel('region');
      setChartTimeframeGrain('period');
      setChartDrillDepth('region');
      setChartDrillRegion(null);
      setChartDrillMarket(null);
      setChartSortBy('');
      setChartSortDirection('DESC');
      setChartSearchQuery('');

      setBottomPerformingSortColumn('NON_ASN_LOADS');
      setBottomPerformingSortDirection('DESC');
      loadBottomPerformingNonAsn(
        buildAsnBottomPerformingRequest({
          regions: [],
          markets: [],
          shippingLocations: [],
          receivingLocations: [],
          years: [resetYear],
          periods: initialPeriodFromCurrentWeek,
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin: '',
          caseQtyMax: '',
          locationHierarchy: 'region',
          sortColumn: 'NON_ASN_LOADS',
          sortDirection: 'DESC',
        }),
      );
      loadNonAsnLoadTrend(
        buildAsnNonAsnLoadTrendRequest({
          regions: [],
          markets: [],
          shippingLocations: [],
          receivingLocations: [],
          years: [resetYear],
          periods: initialPeriodFromCurrentWeek,
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin: '',
          caseQtyMax: '',
          timeframeGrain: 'period',
        }),
      );
      loadLoadsVsNonAsnChart(
        buildAsnLoadsVsNonAsnChartRequest({
          regions: [],
          markets: [],
          shippingLocations: [],
          receivingLocations: [],
          years: [resetYear],
          periods: initialPeriodFromCurrentWeek,
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin: '',
          caseQtyMax: '',
          sortBy: '',
          sortDirection: 'DESC',
          searchQuery: '',
          locationHierarchy: 'region',
          parentGrain: undefined,
          parentGrainVals: undefined,
        }),
      );
    }
    // Only refresh Load Details on Reset when the Load Details tab is the one
    // currently active — mirrors the Overview-tab guard above so switching
    // tabs never triggers the other tab's requests. (By the time this tab is
    // active, its lazy first-activation fetch has already fired, so there is
    // always something to refresh here.) Table pageNumber resets to 1 per
    // spec; the table's own sort/page-size/search selections are NOT
    // filter-bar state and are intentionally left as-is.
    if (activeTab === 'load-details') {
      dispatch(setAsnLoadDetailsPageNumber(1));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: [],
          markets: [],
          shippingLocations: [],
          receivingLocations: [],
          years: [resetYear],
          periods: initialPeriodFromCurrentWeek,
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin: '',
          caseQtyMax: '',
          pageNumber: 1,
          pageSize: ldPageSize,
          sortBy: ldSortBy,
          sortDirection: ldSortDirection,
          searchText: ldSearchText,
        }),
      );
    }
    // Only refresh Location Summary on Reset when that tab is the one
    // currently active — mirrors the Overview/Load Details guards above.
    // LocationSummary itself owns the actual request (it needs its own
    // perspective/grainSelections/sort/search/pagination state, which live
    // in the asnLocationSummary Redux slice) — bumping applyVersion is the
    // signal it watches to know the shared filters were just reset.
    if (activeTab === 'location-summary' || activeTab === 'location-summary-v2') {
      dispatch(bumpAsnLocationSummaryApplyVersion());
    }
  };

  const currentFilterParams = useMemo<AsnGapFilterParams>(() => {
    const params: AsnGapFilterParams = {};

    if (selectedLocations.length > 0 && selectedLocations.length < locationOptions.length) {
      params.location = selectedLocations.map((l) => l.displayText).join(',');
    } else if (selectedMarkets.length > 0) {
      params.market = selectedMarkets.map((m) => String(m.id)).join(',');
      if (selectedRegions.length > 0) {
        params.region = selectedRegions.map((r) => String(r.id)).join(',');
      }
    } else if (selectedRegions.length > 0) {
      params.region = selectedRegions.map((r) => String(r.id)).join(',');
    }

    if (selectedYears.length > 0) params.year = selectedYears.map((y) => String(y.id)).join(',');

    if (selectedWeeks.length > 0) {
      const periodsFromWeeks = new Set<string>();
      const weekInPeriodValues: string[] = [];
      selectedWeeks.forEach((w) => {
        const match = String(w.displayText).match(/\(P(\d+)W(\d+)\)/);
        if (match) {
          periodsFromWeeks.add(match[1]);
          weekInPeriodValues.push(match[2]);
        }
      });
      if (weekInPeriodValues.length > 0) {
        params.week = weekInPeriodValues.join(',');
        const explicitPeriods = selectedPeriods.map((p) => String(p.id).replace(/^P/i, ''));
        const allPeriods = Array.from(new Set([...explicitPeriods, ...periodsFromWeeks]));
        if (allPeriods.length > 0) params.period = allPeriods.join(',');
      }
    } else if (selectedPeriods.length > 0) {
      params.period = selectedPeriods.map((p) => String(p.id).replace(/^P/i, '')).join(',');
    }

    if (deliveryDateRange.start) params.startDate = deliveryDateRange.start;
    if (deliveryDateRange.end) params.endDate = deliveryDateRange.end;

    if (selectedShippingLocations.length > 0) {
      params.shippingLocation = selectedShippingLocations.map((l) => l.displayText).join(',');
    }
    if (selectedReceivingLocations.length > 0) {
      params.receivingLocation = selectedReceivingLocations.map((l) => l.displayText).join(',');
    }
    if (caseQtyMin.trim()) params.caseQtyMin = caseQtyMin.trim();
    if (caseQtyMax.trim()) params.caseQtyMax = caseQtyMax.trim();

    return params;
  }, [
    selectedRegions,
    selectedMarkets,
    selectedLocations,
    selectedYears,
    selectedPeriods,
    selectedWeeks,
    deliveryDateRange,
    locationOptions,
    selectedShippingLocations,
    selectedReceivingLocations,
    caseQtyMin,
    caseQtyMax,
  ]);

  const isApplyEnabled = useMemo(() => {
    // Disable if case quantity validation fails
    if (caseQtyError) return false;

    const normalize = (p: AsnGapFilterParams) => {
      const out: { [k: string]: string[] } = {};
      Object.keys(p).forEach((k) => {
        const v = (p as any)[k];
        if (v == null || v === '') return;
        out[k] = String(v)
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== '')
          .sort();
      });
      return out;
    };

    const a = normalize(currentFilterParams);
    const b = normalize(manualBaselineParams || {});

    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length) return true;
    for (let i = 0; i < aKeys.length; i++) {
      if (aKeys[i] !== bKeys[i]) return true;
      const ak = a[aKeys[i]];
      const bk = b[bKeys[i]];
      if (ak.length !== bk.length) return true;
      for (let j = 0; j < ak.length; j++) if (ak[j] !== bk[j]) return true;
    }
    return false;
  }, [currentFilterParams, manualBaselineParams, caseQtyError]);

  const handleApplyFilters = () => {
    let filtersToApply: AsnGapFilterParams = { ...currentFilterParams };
    // Mirrors filtersToApply's year/period/week clearing below — kept as
    // separate locals (rather than re-reading selectedYears/Periods/Weeks
    // state) since the setSelectedYears([]) etc. calls above are async and
    // would not be reflected yet if read back out in this same tick.
    let effectiveYears = selectedYears;
    let effectivePeriods = selectedPeriods;
    let effectiveWeeks = selectedWeeks;

    if (isDeliveryDateOutsideConstrainedScope) {
      setSelectedYears([]);
      setSelectedPeriods([]);
      setSelectedWeeks([]);
      setActiveQuickSelect(null);
      const { year, period, week, ...rest } = filtersToApply;
      filtersToApply = rest;
      effectiveYears = [];
      effectivePeriods = [];
      effectiveWeeks = [];
    }

    setAppliedFilterParams(filtersToApply);
    setManualBaselineParams(filtersToApply);

    // Only refresh the Overview tab's KPI cards / Bottom Performing / Trend
    // requests when the Overview tab is the one currently active — Apply is
    // fired from the shared filter bar, but Location Summary and Load
    // Details have no use for these three responses, so don't waste a
    // network call fetching them while the user is on another tab.
    if (activeTab === 'overview') {
      // Refresh the KPI cards with the newly-applied filters (including the
      // Year dropdown's current selection — never hardcoded).
      loadAsnOverviewKpis(
        buildAsnOverviewKpiRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: effectiveYears,
          periods: effectivePeriods,
          weeks: effectiveWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
        }),
      );
      // Apply itself doesn't necessarily change any of the filter-watching
      // effect's dependencies (e.g. re-applying the same values, or a
      // delivery-date-only change already reflected there) — fire explicitly
      // so Bottom Performing always refreshes on Apply per spec.
      loadBottomPerformingNonAsn(
        buildAsnBottomPerformingRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: effectiveYears,
          periods: effectivePeriods,
          weeks: effectiveWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          locationHierarchy: chartHierarchyLevel,
          sortColumn: bottomPerformingSortColumn,
          sortDirection: bottomPerformingSortDirection,
        }),
      );
      // Same reasoning as Bottom Performing above — fire explicitly so the
      // Non-ASN Load % Trend chart always refreshes on Apply per spec.
      loadNonAsnLoadTrend(
        buildAsnNonAsnLoadTrendRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: effectiveYears,
          periods: effectivePeriods,
          weeks: effectiveWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          timeframeGrain: chartTimeframeGrain,
        }),
      );
      // Same reasoning as Bottom Performing/Trend above — fire explicitly so
      // the Loads vs Non-ASN chart always refreshes on Apply per spec. Apply
      // only touches the shared filter bar, not the chart's own sort/search/
      // drill-path state — reused as-is (never cleared here).
      loadLoadsVsNonAsnChart(
        buildAsnLoadsVsNonAsnChartRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: effectiveYears,
          periods: effectivePeriods,
          weeks: effectiveWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          sortBy: chartSortBy,
          sortDirection: chartSortDirection,
          searchQuery: chartSearchQuery,
          locationHierarchy: chartHierarchyLevel,
          parentGrain: chartDrillMarket ? 'market' : (chartDrillRegion ? 'region' : undefined),
          parentGrainVals: chartDrillMarket && chartDrillRegion
            ? { region: chartDrillRegion, market: chartDrillMarket }
            : (chartDrillRegion ? { region: chartDrillRegion } : undefined),
        }),
      );
    }
    // Only refresh Load Details on Apply when the Load Details tab is the one
    // currently active — mirrors the Overview-tab guard above so switching
    // tabs never triggers the other tab's requests. (By the time this tab is
    // active, its lazy first-activation fetch has already fired, so there is
    // always something to refresh here.) Table pageNumber resets to 1 per
    // spec; the table's own sort/page-size/search selections are NOT
    // filter-bar state and are intentionally left as-is.
    if (activeTab === 'load-details') {
      dispatch(setAsnLoadDetailsPageNumber(1));
      loadAsnLoadDetails(
        buildAsnLoadDetailsRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: effectiveYears,
          periods: effectivePeriods,
          weeks: effectiveWeeks,
          deliveryDateRange,
          caseQtyMin,
          caseQtyMax,
          pageNumber: 1,
          pageSize: ldPageSize,
          sortBy: ldSortBy,
          sortDirection: ldSortDirection,
          searchText: ldSearchText,
        }),
      );
    }
    // Only refresh Location Summary on Apply when that tab is the one
    // currently active — mirrors the Overview/Load Details guards above.
    if (activeTab === 'location-summary' || activeTab === 'location-summary-v2') {
      dispatch(bumpAsnLocationSummaryApplyVersion());
    }
  };

  const handleQuickSelectPress = (key: QuickSelectKey) => {
    if (activeQuickSelect === key) {
      const clearedParams: AsnGapFilterParams = { ...currentFilterParams };
      delete clearedParams.year;
      delete clearedParams.period;
      delete clearedParams.week;
      delete clearedParams.startDate;
      delete clearedParams.endDate;

      setSelectedYears([]);
      setSelectedPeriods([]);
      setSelectedWeeks([]);
      setDeliveryDateRange({ start: null, end: null });
      setActiveQuickSelect(null);
      setAppliedFilterParams(clearedParams);
      setManualBaselineParams(clearedParams);

      loadAsnOverviewKpis(
        buildAsnOverviewKpiRequest({
          regions: selectedRegions,
          markets: selectedMarkets,
          shippingLocations: selectedShippingLocations,
          receivingLocations: selectedReceivingLocations,
          years: [],
          periods: [],
          weeks: [],
          deliveryDateRange: { start: null, end: null },
          caseQtyMin,
          caseQtyMax,
        }),
      );
      return;
    }

    applyQuickSelect(key);
  };

  const buildYearItem = (year: number): KPIFilterDropdownItem => ({ id: String(year), displayText: String(year) });
  const buildPeriodItems = (periods: number[]): KPIFilterDropdownItem[] =>
    periods.map((p) => ({ id: `P${p}`, displayText: `P${p}` }));
  const buildWeekItems = (entries: { period: number; weekInPeriod: number }[]): KPIFilterDropdownItem[] =>
    entries.map(({ period, weekInPeriod }) => {
      const weekInYear = (period - 1) * WEEKS_PER_PERIOD + weekInPeriod;
      return {
        id: `P${period}W${weekInPeriod}`,
        displayText: `W${weekInYear} (P${period}W${weekInPeriod})`,
        weekInYearNum: weekInYear,
      };
    });

  const applyQuickSelect = (key: QuickSelectKey) => {
    if (!currentPeriodWeek?.year || !currentPeriodWeek?.period || !currentPeriodWeek?.week) return;
    const { year, period, week } = currentPeriodWeek;

    let targetYear = year;
    let periods: number[] = [period];
    let weekEntries: { period: number; weekInPeriod: number }[] = [];
    let endsToday = true;

    switch (key) {
      case 'wtd': {
        weekEntries = [{ period, weekInPeriod: week }];
        break;
      }
      case 'ptd': {
        for (let w = 1; w <= week; w++) weekEntries.push({ period, weekInPeriod: w });
        break;
      }
      case 'qtd': {
        const quarterStart = [...QUARTER_START_PERIODS].reverse().find((qs) => qs <= period) ?? 1;
        periods = [];
        for (let p = quarterStart; p <= period; p++) {
          periods.push(p);
          const maxWeek = p === period ? week : WEEKS_PER_PERIOD;
          for (let w = 1; w <= maxWeek; w++) weekEntries.push({ period: p, weekInPeriod: w });
        }
        break;
      }
      case 'ytd': {
        periods = [];
        for (let p = 1; p <= period; p++) {
          periods.push(p);
          const maxWeek = p === period ? week : WEEKS_PER_PERIOD;
          for (let w = 1; w <= maxWeek; w++) weekEntries.push({ period: p, weekInPeriod: w });
        }
        break;
      }
      case 'lcw': {
        endsToday = false;
        let lcPeriod = period;
        let lcWeek = week - 1;
        if (lcWeek < 1) {
          lcPeriod = period - 1;
          lcWeek = WEEKS_PER_PERIOD;
          if (lcPeriod < 1) {
            targetYear = year - 1;
            lcPeriod = 13;
          }
        }
        periods = [lcPeriod];
        weekEntries = [{ period: lcPeriod, weekInPeriod: lcWeek }];
        break;
      }
      case 'lcp': {
        endsToday = false;
        let lcPeriod = period - 1;
        if (lcPeriod < 1) {
          targetYear = year - 1;
          lcPeriod = 13;
        }
        periods = [lcPeriod];
        for (let w = 1; w <= WEEKS_PER_PERIOD; w++) weekEntries.push({ period: lcPeriod, weekInPeriod: w });
        break;
      }
      default:
        return;
    }

    const nextYears = [buildYearItem(targetYear)];
    const nextPeriods = buildPeriodItems(periods);
    const nextWeeks = buildWeekItems(weekEntries);

    const selectedRange = getScopedDateRangeFromPeriodWeeks([targetYear], periods, weekEntries);
    if (!selectedRange) return;

    const startISO = selectedRange.start;
    const endISO = endsToday ? toISODate(new Date()) : selectedRange.end;

    setSelectedYears(nextYears);
    setSelectedPeriods(nextPeriods);
    setSelectedWeeks(nextWeeks);
    setDeliveryDateRange({ start: startISO, end: endISO });
    setActiveQuickSelect(key);

    const quickSelectParams: AsnGapFilterParams = {
      ...currentFilterParams,
      year: String(targetYear),
      period: periods.join(','),
      week: weekEntries.map(({ weekInPeriod }) => String(weekInPeriod)).join(','),
      startDate: startISO,
      endDate: endISO,
    };

    setAppliedFilterParams(quickSelectParams);
    setManualBaselineParams(quickSelectParams);

    loadAsnOverviewKpis(
      buildAsnOverviewKpiRequest({
        regions: selectedRegions,
        markets: selectedMarkets,
        shippingLocations: selectedShippingLocations,
        receivingLocations: selectedReceivingLocations,
        years: nextYears,
        periods: nextPeriods,
        weeks: nextWeeks,
        deliveryDateRange: { start: startISO, end: endISO },
        caseQtyMin,
        caseQtyMax,
      }),
    );
  };

  // ─── Chart Configuration bar (Overview tab only) ─────────────────────────
  const renderChartConfigurationBar = () => {
    if (activeTab !== 'overview') return null;
    const chartConfigCountLabel = '2 / 2';
    return (
      <View style={styles.chartConfigWrapper}>
        <View style={styles.chartConfigCard}>
          <TouchableOpacity
            style={styles.chartConfigHeader}
            onPress={() => setIsChartConfigExpanded((prev) => !prev)}
            accessibilityRole="button"
            accessibilityLabel="Chart Configuration"
            testID="asn-gap-chart-config-toggle"
          >
            <View style={styles.chartConfigTitleRow}>
              <MaterialIcons name="tune" size={18} color="#005CBC" />
              <Text style={styles.chartConfigLabel}>Chart Configuration</Text>
              <View style={styles.chartConfigCountPill}>
                <Text style={styles.chartConfigCountText}>{chartConfigCountLabel}</Text>
              </View>
              {Platform.OS === 'web' && (
                <Text style={styles.chartConfigDescription} numberOfLines={1}>
                  Grouping and timeframe for every chart on this tab
                </Text>
              )}
            </View>
            <View style={styles.chartConfigChevronWrap}>
              <MaterialIcons
                name={isChartConfigExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                size={24}
                color="#616161"
              />
            </View>
          </TouchableOpacity>

          {isChartConfigExpanded && (
            <View style={[styles.chartConfigBody, isMobile && { width: '95%'}]}>
              <View style={styles.chartConfigGroup}>
                <Text style={styles.chartConfigGroupLabel}>Location Hierarchy</Text>
                <View style={styles.segmentedControl}>
                  {CHART_HIERARCHY_OPTIONS.map((opt, idx) => {
                    const isActive = chartHierarchyLevel === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        style={[
                          styles.segmentedOption,
                          idx > 0 && styles.segmentedOptionDivider,
                          isActive && styles.segmentedOptionActive,
                        ]}
                        onPress={() => handleChartHierarchySelect(opt.key)}
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                        testID={`asn-gap-chart-hierarchy-${opt.key}`}
                      >
                        <Text style={[styles.segmentedOptionText, isActive && styles.segmentedOptionTextActive]}>
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>

              <View
                style={[
                  styles.chartConfigGroup,
                  isMobile && {
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    width: '100%',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.chartConfigGroupLabel,
                    isMobile && { marginBottom: 0 },
                  ]}
                >
                  Timeframe
                </Text>
                <View
                  style={[
                    styles.segmentedControl,
                    isMobile && { alignSelf: 'stretch' },
                  ]}
                >
                  {CHART_TIMEFRAME_OPTIONS.map((opt, idx) => {
                    const isActive = chartTimeframeGrain === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        style={[
                          styles.segmentedOption,
                          idx > 0 && styles.segmentedOptionDivider,
                          isActive && styles.segmentedOptionActive,
                          isMobile && {
                            flex: 1,
                            minWidth: 0,
                            alignItems: 'center',
                            justifyContent: 'center',
                          },
                        ]}
                        onPress={() => setChartTimeframeGrain(opt.key)}
                        accessibilityRole="button"
                        accessibilityLabel={opt.label}
                        testID={`asn-gap-chart-timeframe-${opt.key}`}
                      >
                        <Text
                          style={[
                            styles.segmentedOptionText,
                            isActive && styles.segmentedOptionTextActive,
                            isMobile && { textAlign: 'center' },
                          ]}
                        >
                          {opt.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    );
  };

  if (isMobile && mobileFilterOverlayOpen) {
    return (
      <View style={styles.mobileOverlayContainer}>
        {/* Scrollable Filter List */}
        <ScrollView style={styles.mobileOverlayBody} contentContainerStyle={styles.mobileOverlayBodyContent} keyboardShouldPersistTaps="handled">
          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>SHIPPING REGION / UNIT</Text>
            <KPIFilterDropdown
              childList={regionOptions}
              selectedValue={selectedRegions}
              setSelectedValue={handleRegionChange}
              placeholder="All"
              isOpen={openFilterKey === 'region'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)}
              testID="asn-gap-filter-region"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>SHIPPING MARKET / POD</Text>
            <KPIFilterDropdown
              childList={marketOptions}
              selectedValue={selectedMarkets}
              setSelectedValue={handleMarketChange}
              placeholder="All"
              isOpen={openFilterKey === 'market'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)}
              testID="asn-gap-filter-market"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>SHIPPING LOCATION</Text>
            <KPIFilterDropdown
              childList={locationOptions}
              selectedValue={selectedShippingLocations}
              setSelectedValue={handleShippingLocationChange}
              placeholder="All"
              isOpen={openFilterKey === 'shippingLocation'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'shippingLocation' : null)}
              testID="asn-gap-filter-shipping-location"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>RECEIVING LOCATION</Text>
            <KPIFilterDropdown
              childList={receivingLocationOptions}
              selectedValue={selectedReceivingLocations}
              setSelectedValue={setSelectedReceivingLocations}
              placeholder="All"
              isOpen={openFilterKey === 'receivingLocation'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'receivingLocation' : null)}
              testID="asn-gap-filter-receiving-location"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>YEAR</Text>
            <KPIFilterDropdown
              childList={yearOptions}
              selectedValue={selectedYears}
              setSelectedValue={handleYearChange}
              placeholder="All"
              isOpen={openFilterKey === 'year'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)}
              testID="asn-gap-filter-year"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>PERIOD</Text>
            <KPIFilterDropdown
              childList={periodOptions}
              selectedValue={selectedPeriods}
              setSelectedValue={handlePeriodChange}
              placeholder="All"
              isOpen={openFilterKey === 'period'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)}
              testID="asn-gap-filter-period"
              disabledIds={disabledPeriodIds}
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>WEEK IN YEAR</Text>
            <KPIFilterDropdown
              childList={weekOptions}
              selectedValue={selectedWeeks}
              setSelectedValue={handleWeekChange}
              placeholder="All"
              isOpen={openFilterKey === 'week'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)}
              testID="asn-gap-filter-week"
              disabledIds={disabledWeekIds}
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <View style={styles.filterLabelRow}>
              <Text style={styles.mobileOverlayFilterLabel}>DATE</Text>
              {deliveryDateDisabledReason ? (
                <Text style={styles.filterHintText}>{deliveryDateDisabledReason}</Text>
              ) : null}
            </View>
            <DeliveryDateRangeFilter
              value={deliveryDateRange}
              onChange={handleDeliveryDateChange}
              minDate={constrainedDeliveryDateScope?.start}
              maxDate={constrainedDeliveryDateScope?.end}
              disabled={!!deliveryDateDisabledReason}
              isOpen={openFilterKey === 'deliveryDate'}
              onOpenChange={(open) => setOpenFilterKey(open ? 'deliveryDate' : null)}
              testID="asn-gap-filter-delivery-date"
            />
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>CASE QUANTITY</Text>
            <View style={styles.rangePair}>
              <TextInput
                style={styles.rangeInput}
                value={caseQtyMin}
                onChangeText={(text) => setCaseQtyMin(text.replace(/[^0-9]/g, ''))}
                placeholder="Min"
                placeholderTextColor="#9AA1AB"
                keyboardType="numeric"
                testID="asn-gap-filter-case-qty-min"
              />
              <Text style={styles.rangeDash}>to</Text>
              <TextInput
                style={styles.rangeInput}
                value={caseQtyMax}
                onChangeText={(text) => setCaseQtyMax(text.replace(/[^0-9]/g, ''))}
                placeholder="Max"
                placeholderTextColor="#9AA1AB"
                keyboardType="numeric"
                testID="asn-gap-filter-case-qty-max"
              />
            </View>
            {caseQtyError && (
              <Text style={styles.caseQtyErrorText}>{caseQtyError}</Text>
            )}
          </View>

          <View style={styles.mobileOverlayFilterRow}>
            <Text style={styles.mobileOverlayFilterLabel}>TIMEFRAME</Text>
            <View style={styles.quickSelectRow}>
              {QUICK_SELECT_OPTIONS.map((opt) => {
                const isActive = activeQuickSelect === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.quickSelectPill, isActive && styles.quickSelectPillActive]}
                    onPress={() => handleQuickSelectPress(opt.key)}
                    accessibilityRole="button"
                    accessibilityLabel={opt.label}
                    testID={`asn-gap-quick-select-${opt.key}`}
                  >
                    <Text style={[styles.quickSelectPillText, isActive && styles.quickSelectPillTextActive]}>
                      {mobileLabelMap[opt.key] ?? opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </ScrollView>

        {/* Action Buttons Footer */}
        <View style={styles.mobileOverlayFooter}>
          <TouchableOpacity
            style={styles.mobileOverlayCancelBtn}
            onPress={() => setMobileFilterOverlayOpen(false)}
            accessibilityRole="button"
            accessibilityLabel="Close filters"
          >
            <Text style={styles.mobileOverlayCancelBtnText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.mobileOverlayResetBtn}
            onPress={() => {
              handleResetFilters();
              setMobileFilterOverlayOpen(false);
            }}
            accessibilityRole="button"
            accessibilityLabel="Reset filters"
          >
            <Text style={styles.mobileOverlayResetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.mobileOverlayApplyBtn, !isApplyEnabled && { opacity: 0.5 }]}
            disabled={!isApplyEnabled}
            onPress={() => {
              handleApplyFilters();
              setMobileFilterOverlayOpen(false);
            }}
            accessibilityRole="button"
            accessibilityLabel="Apply filters"
          >
            <Text style={styles.mobileOverlayApplyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator
    >
      {/* Breadcrumb */}
      <View style={styles.breadcrumbRow}>
        <DLOSimpleBreadcrumb
          path={["Analytics Hub", title]}
          onHomePress={() => {
            // Prefer in-app navigation helper when available to avoid full reloads
            const nav = (globalThis as any)?.appNavigate;
            if (typeof nav === 'function') {
              nav('Home');
              return;
            }

            // Fallback: web history replace then dispatch popstate
            if (Platform.OS === 'web') {
              try {
                window.history.pushState({}, '', '/home');
                window.dispatchEvent(new PopStateEvent('popstate'));
              } catch {
                window.location.href = '/home';
              }
            } else {
              if (typeof window !== 'undefined') window.location.href = '/home';
            }
          }}
          onNavigate={(index, label) => {
            if (index === 0) {
              const nav = (globalThis as any)?.appNavigate;
              if (typeof nav === 'function') {
                nav('Analytics', { tab: 'all-reports' });
                return;
              }

              if (Platform.OS === 'web') {
                try {
                  window.history.pushState({}, '', '/analytics');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                } catch {
                  window.location.href = '/analytics';
                }
              }
            }
          }}
        />
      </View>

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <MaterialIcons name={iconName as any} size={28} color="#005CBC" />
          <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit={false}>
            {title}
          </Text>
        </View>
        <View style={styles.headerRight}>
          {!!lastRefreshedLabel && (
            <View style={styles.refreshedBadge}>
              <Text style={styles.refreshedBadgeText}>
                {'Refreshed: '}
                <Text style={styles.refreshedBadgeValueText}>{lastRefreshedLabel}</Text>
              </Text>
            </View>
          )}
        </View>
      </View>
      <Text style={styles.subtitle}>
        Loads shipped or received without an Advance Ship Notice, by shipping and receiving location
      </Text>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabBar}
        contentContainerStyle={styles.tabBarContent}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, isActive && styles.tabActive]}
              onPress={() => setActiveTab(tab.key)}
              activeOpacity={0.7}
            >
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>{tab.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {isMobile ? (
        <View style={styles.mobileFilterTriggerRow}>
          <TouchableOpacity
            style={styles.mobileFilterButton}
            onPress={() => setMobileFilterOverlayOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Open filters"
          >
            <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
            <Text style={styles.mobileFilterButtonText}>Filter</Text>
          </TouchableOpacity>
        </View>
      ) : (
        /* Filter Bar — shared across all three tabs */
        <View style={styles.filterBarWrapper}>
          <View style={styles.filterBar}>
            <View style={styles.filtersRow}>
              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>SHIPPING REGION / UNIT</Text>
                <KPIFilterDropdown
                  childList={regionOptions}
                  selectedValue={selectedRegions}
                  setSelectedValue={handleRegionChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'region'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)}
                  testID="asn-gap-filter-region"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>SHIPPING MARKET / POD</Text>
                <KPIFilterDropdown
                  childList={marketOptions}
                  selectedValue={selectedMarkets}
                  setSelectedValue={handleMarketChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'market'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)}
                  testID="asn-gap-filter-market"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>SHIPPING LOCATION</Text>
                <KPIFilterDropdown
                  childList={locationOptions}
                  selectedValue={selectedShippingLocations}
                  setSelectedValue={handleShippingLocationChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'shippingLocation'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'shippingLocation' : null)}
                  testID="asn-gap-filter-shipping-location"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>RECEIVING LOCATION</Text>
                <KPIFilterDropdown
                  childList={receivingLocationOptions}
                  selectedValue={selectedReceivingLocations}
                  setSelectedValue={setSelectedReceivingLocations}
                  placeholder="All"
                  isOpen={openFilterKey === 'receivingLocation'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'receivingLocation' : null)}
                  testID="asn-gap-filter-receiving-location"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>YEAR</Text>
                <KPIFilterDropdown
                  childList={yearOptions}
                  selectedValue={selectedYears}
                  setSelectedValue={handleYearChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'year'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)}
                  testID="asn-gap-filter-year"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>PERIOD</Text>
                <KPIFilterDropdown
                  childList={periodOptions}
                  selectedValue={selectedPeriods}
                  setSelectedValue={handlePeriodChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'period'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)}
                  testID="asn-gap-filter-period"
                  disabledIds={disabledPeriodIds}
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>WEEK IN YEAR</Text>
                <KPIFilterDropdown
                  childList={weekOptions}
                  selectedValue={selectedWeeks}
                  setSelectedValue={handleWeekChange}
                  placeholder="All"
                  isOpen={openFilterKey === 'week'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)}
                  testID="asn-gap-filter-week"
                  disabledIds={disabledWeekIds}
                />
              </View>

              <View style={[styles.filterItem, styles.deliveryDateFilterItem]}>
                <View style={styles.filterLabelRow}>
                  <Text style={[styles.filterLabel, styles.filterLabelInRow]}>DATE</Text>
                  {deliveryDateDisabledReason ? (
                    <Text style={styles.filterHintText}>{deliveryDateDisabledReason}</Text>
                  ) : null}
                </View>
                <DeliveryDateRangeFilter
                  value={deliveryDateRange}
                  onChange={handleDeliveryDateChange}
                  minDate={constrainedDeliveryDateScope?.start}
                  maxDate={constrainedDeliveryDateScope?.end}
                  disabled={!!deliveryDateDisabledReason}
                  isOpen={openFilterKey === 'deliveryDate'}
                  onOpenChange={(open) => setOpenFilterKey(open ? 'deliveryDate' : null)}
                  testID="asn-gap-filter-delivery-date"
                />
              </View>

              <View style={styles.filterItem}>
                <Text style={styles.filterLabel}>CASE QUANTITY</Text>
                <View style={styles.rangePair}>
                  <TextInput
                    style={styles.rangeInput}
                    value={caseQtyMin}
                    onChangeText={(text) => setCaseQtyMin(text.replace(/[^0-9]/g, ''))}
                    placeholder="Min"
                    placeholderTextColor="#9AA1AB"
                    keyboardType="numeric"
                    testID="asn-gap-filter-case-qty-min"
                  />
                  <Text style={styles.rangeDash}>to</Text>
                  <TextInput
                    style={styles.rangeInput}
                    value={caseQtyMax}
                    onChangeText={(text) => setCaseQtyMax(text.replace(/[^0-9]/g, ''))}
                    placeholder="Max"
                    placeholderTextColor="#9AA1AB"
                    keyboardType="numeric"
                    testID="asn-gap-filter-case-qty-max"
                  />
                </View>
                {caseQtyError && (
                  <Text style={styles.caseQtyErrorText}>{caseQtyError}</Text>
                )}
              </View>

              {/* Reset & Apply buttons */}
              <View style={styles.filterActions}>
                <TouchableOpacity style={styles.resetBtn} onPress={handleResetFilters} testID="asn-gap-filter-reset">
                  <Text style={styles.resetBtnText}>Reset</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.applyBtn, isApplyEnabled ? styles.applyBtnEnabled : styles.applyBtnDisabled]}
                  onPress={handleApplyFilters}
                  disabled={!isApplyEnabled}
                  testID="asn-gap-filter-apply"
                >
                  <Text style={[styles.applyBtnText, isApplyEnabled && styles.applyBtnTextEnabled]}>Apply</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.filterDivider} />

            {/* Timeframe quick-select — applies immediately and can be toggled off */}
            <View style={styles.timeframeRow}>
              <Text style={styles.timeframeLabel}>TIMEFRAME</Text>
              <View style={styles.quickSelectRow}>
                {QUICK_SELECT_OPTIONS.map((opt) => {
                  const isActive = activeQuickSelect === opt.key;
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={[styles.quickSelectPill, isActive && styles.quickSelectPillActive]}
                      onPress={() => handleQuickSelectPress(opt.key)}
                      accessibilityRole="button"
                      accessibilityLabel={opt.label}
                      testID={`asn-gap-quick-select-${opt.key}`}
                    >
                      <Text style={[styles.quickSelectPillText, isActive && styles.quickSelectPillTextActive]}>
                        {opt.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          </View>
        </View>
      )}

      {/* Chart Configuration — below the global filter bar, Overview tab only */}
      <View style={{ paddingHorizontal: 24 }}>
        {renderChartConfigurationBar()}
      </View>

      <View style={styles.tabContentContainer}>
        {activeTab === 'overview' && (
          <Overview
            hierarchyLevel={chartDrillDepth}
            kpiData={kpiData}
            kpiLoading={kpiLoading}
            bottomPerformingData={bottomPerformingData}
            bottomPerformingLoading={bottomPerformingLoading}
            bottomPerformingError={bottomPerformingError}
            bottomPerformingSortColumn={bottomPerformingSortColumn}
            bottomPerformingSortDirection={bottomPerformingSortDirection}
            onBottomPerformingSortChange={handleBottomPerformingSortChange}
            onBottomPerformingRetry={handleBottomPerformingRetry}
            timeframeLevel={chartTimeframeGrain}
            trendData={trendData}
            trendLoading={trendLoading}
            trendError={trendError}
            onTrendRetry={handleTrendRetry}
            loadsVsNonAsnData={loadsVsNonAsnData}
            loadsVsNonAsnLoading={loadsVsNonAsnLoading}
            loadsVsNonAsnError={loadsVsNonAsnError}
            drillRegion={chartDrillRegion}
            drillMarket={chartDrillMarket}
            drillOptionValues={chartDrillOptionValues}
            drillOptionsLoading={chartDrillOptionsLoading}
            onLoadsVsNonAsnBarPress={handleLoadsVsNonAsnBarPress}
            onLoadsVsNonAsnBack={handleLoadsVsNonAsnBack}
            onLoadsVsNonAsnNavigateToLevel={handleLoadsVsNonAsnNavigateToLevel}
            loadsVsNonAsnSortBy={chartSortBy}
            onLoadsVsNonAsnSortChange={handleLoadsVsNonAsnSortChange}
            loadsVsNonAsnSearchQuery={chartSearchQuery}
            onLoadsVsNonAsnSearchChange={setChartSearchQuery}
            onLoadsVsNonAsnRetry={handleLoadsVsNonAsnRetry}
          />
        )}
        {activeTab === 'location-summary' && <LocationSummary filterInputs={asnFilterInputs} isFilterReady={isFilterReady} />}
        {activeTab === 'location-summary-v2' && (
          <LocationSummaryV2
            filterInputs={asnFilterInputs}
            onExport={handleExportLocationSummaryV2}
            isExporting={isExportingLocationSummaryV2}
            isFilterReady={isFilterReady}
          />
        )}
        {activeTab === 'load-details' && (
          <LoadDetails
            rows={loadDetailsData?.rows ?? []}
            totalCount={loadDetailsData?.totalCount ?? 0}
            pageNumber={loadDetailsData?.pageNumber ?? ldPageNumber}
            pageSize={loadDetailsData?.pageSize ?? ldPageSize}
            totalPages={loadDetailsData?.totalPages ?? 0}
            sortBy={loadDetailsData?.sortBy ?? ldSortBy}
            sortDirection={loadDetailsData?.sortDirection ?? ldSortDirection}
            loading={loadDetailsLoading}
            hasData={loadDetailsData?.hasData ?? false}
            message={loadDetailsError ?? loadDetailsData?.message}
            searchText={ldSearchText}
            onSearchChange={handleLoadDetailsSearchChange}
            onPageChange={handleLoadDetailsPageChange}
            onPageSizeChange={handleLoadDetailsPageSizeChange}
            onSortChange={handleLoadDetailsSortChange}
            onExport={handleExportLoadDetails}
            isExporting={isExportingLoadDetails}
          />
        )}
      </View>

      {/* ASN Gap Overview KPI error/data-quality modal */}
      <DLOModal
        visible={kpiErrorModal.visible}
        onClose={dismissKpiError}
        title={kpiErrorModal.title}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={dismissKpiError}
        hideButtons={false}
        showCloseIcon
      >
        <Text>{kpiErrorModal.message}</Text>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f9ff',
  },
  contentContainer: {
    paddingBottom: 40,
  },
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginHorizontal: 24,
    marginTop: 12,
  },
  refreshedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  refreshedBadgeText: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: FontFamily.regular,
    color: '#4B5563',
  },
  refreshedBadgeValueText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#005CBC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
  },
  subtitle: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6B6E72',
    lineHeight: 20,
    marginHorizontal: 24,
    marginTop: 0,
    marginBottom: 8,
    marginLeft:60
  },
  tabBar: {
    marginHorizontal: 24,
    marginTop: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    paddingBottom: 0,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: '#005CBC',
  },
  tabLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: FontFamily.medium,
    fontWeight: '700',
  },
  tabLabelActive: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  filterBarWrapper: {
    paddingHorizontal: 24,
    paddingTop: 4,
    paddingBottom: 16,
  },
  filterBar: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    padding: 20,
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  filtersRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 10,
  },
  filterItem: {
    minWidth: 140,
    flexGrow: 1,
  },
  deliveryDateFilterItem: {
    minWidth: 260,
    maxWidth: 320,
    flexGrow: 1.2,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  filterLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 8,
    gap: 6,
  },
  filterHintText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#0B5FC6',
  },
  filterLabelInRow: {
    marginBottom: 0,
  },
  rangePair: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rangeInput: {
    height: 40,
    width: 96,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 8,
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#1A1D21',
    backgroundColor: '#FFFFFF',
  },
  rangeDash: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#6A6D71',
  },
  caseQtyErrorText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#D42F2F',
    marginTop: 6,
  },
  filterActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
    marginTop: 20,
  },
  quickSelectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 0,
    flex: 1,
  },
  filterDivider: {
    height: 1,
    backgroundColor: '#E3E9F3',
    marginTop: 16,
    marginBottom: 16,
  },
  timeframeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 0,
  },
  timeframeLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
  },
  quickSelectPill: {
    borderWidth: 1,
    borderColor: '#E3E9F3',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
  },
  quickSelectPillActive: {
    backgroundColor: '#0B5FC6',
    borderColor: '#0B5FC6',
  },
  quickSelectPillText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#3D4147',
  },
  quickSelectPillTextActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: '#0B5FC6',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
    minHeight: 40,
    justifyContent: 'center',
  },
  resetBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#0B5FC6',
  },
  applyBtn: {
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
    minHeight: 40,
    justifyContent: 'center',
  },
  applyBtnEnabled: {
    backgroundColor: '#0B5FC6',
  },
  applyBtnDisabled: {
    backgroundColor: '#D0D5DD',
  },
  applyBtnText: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    color: '#9CA3AF',
  },
  applyBtnTextEnabled: {
    color: '#FFFFFF',
  },
  chartConfigWrapper: {
    paddingBottom: 16,
  },
  chartConfigCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  chartConfigHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 12,
  },
  chartConfigChevronWrap: {
    width: 24,
    height: 24,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chartConfigTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  chartConfigLabel: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
  },
  chartConfigDescription: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#838383',
    flexShrink: 1,
  },
  chartConfigCountPill: {
    backgroundColor: '#0B5FC6',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 14,
  },
  chartConfigCountText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    color: '#FFFFFF',
  },
  chartConfigBody: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    columnGap: 32,
    rowGap: 16,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    marginHorizontal: 16,
    paddingTop: 16,
  },
  chartConfigGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  chartConfigGroupLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#6A6D71',
    letterSpacing: 0.4,
  },
  segmentedControl: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 8,
    overflow: 'hidden',
  },
  segmentedOption: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#F2F4F7',
  },
  segmentedOptionDivider: {
    borderLeftWidth: 1,
    borderLeftColor: '#D0D5DD',
  },
  segmentedOptionActive: {
    backgroundColor: '#0B5FC6',
  },
  segmentedOptionText: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#495057',
  },
  segmentedOptionTextActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  mobileFilterTriggerRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 12,
    marginHorizontal: 24,
    marginTop: 12,
  },
  mobileFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 4,
    paddingHorizontal: 16,
    height: 32,
    gap: 4,
    marginLeft: 'auto',
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  mobileOverlayContainer: {
    flex: 1,
    backgroundColor: '#F5F7FA',
  },
  mobileOverlayBody: {
    flex: 1,
  },
  mobileOverlayBodyContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 4,
  },
  mobileOverlayFilterRow: {
    marginBottom: 16,
  },
  mobileOverlayFilterLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: '#6B6E72',
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  mobileOverlayFooter: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#E3E9F3',
  },
  mobileOverlayCancelBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#DEE2E6',
  },
  mobileOverlayCancelBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#495057',
  },
  mobileOverlayResetBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#DEE2E6',
  },
  mobileOverlayResetBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#CC3333',
  },
  mobileOverlayApplyBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: '#034895',
  },
  mobileOverlayApplyBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
  },
  tabContentContainer: {
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 10,
  },
  emptyTabContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    backgroundColor: '#F5F9FF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E3E9F3',
  },
  emptyTabText: {
    fontSize: 16,
    fontFamily: FontFamily.medium,
    color: '#6B6E72',
    fontWeight: '500',
  },
});

export default AsnGapDashboard;
